import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Download } from 'lucide-react';
import { ArtworkColorExtractor, ColorExtractionResult } from '@/utils/artworkColorExtractor';
import prayerTimeService from '@/services/prayerTimeService';
import musicScheduleService from '@/services/musicScheduleService';
import playbackStatusService from '@/services/playbackStatusService';
import specialDayService from '@/services/specialDayService';
import websocketService from '@/services/websocketService';
import { MusicSchedule } from '@/types/schedule';
import { buildEnterprisePlaylist, recordEnterpriseSongPlay } from '@/utils/enterprisePlaylistEngine';
import PlaybackQueueEngine from '@/utils/PlaybackQueueEngine';
import AudioPlaybackEngine from '@/utils/AudioPlaybackEngine';
import { CONFIG } from '@/config';
import { localStateStore } from '@/utils/localStateStore';

interface Song {
  _id: string;
  title: string;
  name: string;
  artist: string;
  filePath: string;
  duration: number;
  cdnUrl?: string;
  artwork?: string;
  isAnnouncement?: boolean;
  announcementId?: string;
  audioFileId?: string;
  announcementScheduleType?: AnnouncementScheduleType;
  scheduledAnnouncementKey?: string;
}

interface Playlist {
  _id: string;
  name: string;
  songs: Song[];
  artwork?: string;
  baseUrl?: string;
  playbackPolicy?: {
    shuffleEnabled?: boolean;
    noRepeatSameDay?: boolean;
    recentCooldownCount?: number;
    maxHistorySize?: number;
  };
}

interface UiSettings {
  crossfade?: {
    enabled?: boolean;
    web?: {
      enabled?: boolean;
    };
    desktop?: {
      enabled?: boolean;
    };
  };
}

type AnnouncementScheduleType = 'songs' | 'minutes' | 'specific';

interface AnnouncementAudioFile {
  url: string;
  status?: string;
  localPath?: string;
  id?: string;
  _id?: string;
  fileName?: string;
}

interface Announcement {
  _id: string;
  title: string;
  content: string;
  scheduleType: AnnouncementScheduleType;
  songInterval?: number;
  minuteInterval?: number;
  specificTimes?: string[];
  activeDays?: number[];
  audioFile?: AnnouncementAudioFile[];
  audioFiles?: AnnouncementAudioFile[];
  startDate: string;
  endDate: string;
}

interface InterruptedPlayback {
  song: Song;
  index: number;
  time: number;
  wasPlaying: boolean;
}

interface PendingResume {
  songId: string;
  time: number;
  shouldPlay: boolean;
}

interface Certificate {
  certificateUrl: string;
  certificateFileName: string;
  title: string;
}

interface PlayerProps {
  playlist: Playlist;
}

const invalidMediaSourceValues = new Set(['', 'undefined', 'null', 'false']);

const normalizeMediaSource = (value?: string | null) => {
  const normalized = String(value || '').trim();
  return invalidMediaSourceValues.has(normalized.toLowerCase()) ? '' : normalized;
};

const resolveSongUrl = (song?: Song | null, baseUrl?: string) => {
  if (!song) return '';

  const directUrl = normalizeMediaSource(song.cdnUrl);
  if (directUrl) return directUrl;

  const filePath = normalizeMediaSource(song.filePath);
  if (!filePath) return '';

  if (/^(https?:|blob:|data:)/i.test(filePath)) {
    return filePath;
  }

  const normalizedBaseUrl = normalizeMediaSource(baseUrl);
  if (!normalizedBaseUrl) {
    return filePath;
  }

  return `${normalizedBaseUrl.replace(/\/+$/, '')}/${filePath.replace(/^\/+/, '')}`;
};

const isPlayInterruptionError = (error: unknown) => {
  return (error as DOMException)?.name === 'AbortError';
};

const readStoredUiSettings = (): UiSettings => {
  return localStateStore.getJson<UiSettings>('uiSettings', {}) || {};
};

const Player: React.FC<PlayerProps> = ({ playlist }) => {
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  // isPlaying durumunu localStorage'dan yükle (sayfa yenilemesinde durumu koru)
  const [isPlaying, setIsPlaying] = useState(() => {
    const savedPlaying = localStateStore.getString('playerIsPlaying');
    return savedPlaying === 'true';
  });
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [uiSettings, setUiSettings] = useState<UiSettings>(() => readStoredUiSettings());

  // Window boyutları - Mobile Native için
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [windowHeight, setWindowHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 768);

  // Mobile ve Landscape kontrolü
  const isMobile = windowWidth < 768;
  const isLandscape = windowWidth > windowHeight;
  const isShortViewport = windowHeight < 770;

  // Volume değerini localStorage'dan yükle, yoksa 80 kullan
  const [volume, setVolume] = useState(() => {
    const savedVolume = localStateStore.getString('playerVolume');
    return savedVolume ? parseInt(savedVolume, 10) : 80;
  });

  // Mute durumunu localStorage'dan yükle
  const [isMuted, setIsMuted] = useState(() => {
    const savedMute = localStateStore.getString('playerMuted');
    return savedMute === 'true';
  });

  // Acil durum durumunu localStorage'dan yükle
  const [isEmergencyStopped, setIsEmergencyStopped] = useState(() => {
    return localStateStore.getString('emergencyStopped') === 'true';
  });
  const [emergencyReason, setEmergencyReason] = useState(() => {
    return localStateStore.getString('emergencyReason') || 'Acil durum';
  });

  // Manager tarafından duraklatılma durumu
  const [isRemotePaused, setIsRemotePaused] = useState(() => {
    return localStateStore.getString('remotePaused') === 'true';
  });

  // Stable color extraction state
  const [colorResult, setColorResult] = useState<ColorExtractionResult | null>(null);
  const [isColorExtracting, setIsColorExtracting] = useState(false);
  
  const [gradientColors, setGradientColors] = useState({
    primary: '25, 25, 45',
    secondary: '60, 60, 90',
    tertiary: '100, 100, 140',
    primaryOpacity: 1,
    secondaryOpacity: 0.8,
    tertiaryOpacity: 0.6,
    isDark: true
  });

  const [enhancedPlaylist, setEnhancedPlaylist] = useState<Playlist>(playlist || { _id: '', name: '', songs: [], artwork: '' });
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [showPrayerModal, setShowPrayerModal] = useState(false);
  const [prayerTimeInfo, setPrayerTimeInfo] = useState<{
    prayerName: string;
    endTime: Date;
  } | null>(null);
  const [certificateData, setCertificateData] = useState<Certificate | null>(null);

  // Music Schedule state (Planlı Çalma Saatleri)
  const [musicSchedule, setMusicSchedule] = useState<MusicSchedule | null>(null);
  const [scheduleState, setScheduleState] = useState<{ active: boolean; message: string }>({ active: false, message: '' });
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [announcementsLoaded, setAnnouncementsLoaded] = useState(false);
  const [isSpecialDayActive, setIsSpecialDayActive] = useState(false);

  // REF kullanarak stale closure sorununu çöz
  const enhancedPlaylistRef = useRef<Playlist>(playlist);
  const announcementsRef = useRef<Announcement[]>([]);
  const specialDayBackupPlaylistRef = useRef<Playlist | null>(null);
  const isSpecialDayActiveRef = useRef(false);

  // 🛡️ STATE-REF SYNC - Desync sorununu çöz
  useEffect(() => {
    enhancedPlaylistRef.current = enhancedPlaylist;
  }, [enhancedPlaylist]);

  useEffect(() => {
    announcementsRef.current = announcements;
  }, [announcements]);

  useEffect(() => {
    isSpecialDayActiveRef.current = isSpecialDayActive;
  }, [isSpecialDayActive]);

  // Window resize listener - Mobile Native için
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    const handleOrientationChange = () => {
      setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  // ✅ ÇÖZÜM: currentSong'u ayrı state olarak tut
  const [currentSong, setCurrentSong] = useState<Song | null>(
    (playlist.songs && playlist.songs.length > 0) ? playlist.songs[0] : null
  );

  const playlistSignature = useMemo(() => {
    return JSON.stringify({
      id: playlist._id,
      name: playlist.name,
      artwork: playlist.artwork,
      songs: (playlist.songs || []).map(song => ({
        id: song._id,
        name: song.name,
        title: song.title,
        artist: song.artist,
        filePath: song.filePath,
        cdnUrl: song.cdnUrl,
        duration: song.duration,
        artwork: song.artwork
      }))
    });
  }, [playlist]);

  const audioRef = useRef<HTMLAudioElement>(null);
  const crossfadeAudioRef = useRef<HTMLAudioElement>(null);
  const playbackEngineRef = useRef(new AudioPlaybackEngine<Song, Playlist>());
  const suppressPauseEventRef = useRef(false);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const announcementCountersRef = useRef<{ [key: string]: number }>({});
  const sameIntervalRotationRef = useRef<{ [key: string]: number }>({});
  const announcementAudioRotationRef = useRef<{ [key: string]: number }>({});
  const songAnnouncementSignatureRef = useRef('');
  const currentSongIndexRef = useRef(0);
  const currentSongRef = useRef<Song | null>(null);
  const timedAnnouncementQueueRef = useRef<Song[]>([]);
  const interruptedPlaybackStackRef = useRef<InterruptedPlayback[]>([]);
  const pendingResumeRef = useRef<PendingResume | null>(null);
  const forceTimedAnnouncementPlaybackRef = useRef<string | null>(null);
  const minuteAnnouncementLastPlayRef = useRef<Record<string, number>>({});
  const processedSpecificAnnouncementsRef = useRef<Set<string>>(new Set());
  const audioRetryRef = useRef<Record<string, number>>({});
  const failedTrackIdsRef = useRef<Set<string>>(new Set());
  const playbackQueueRef = useRef<PlaybackQueueEngine | null>(null);
  const playAttemptRef = useRef(0);
  const lastSongAnnouncementCompletionRef = useRef<{ trackId: string | null; timestamp: number }>({
    trackId: null,
    timestamp: 0
  });
  
  // Çalan şarkıyı takip etmek için ref - veeq-player-desktop gibi
  const currentlyPlayingTrackRef = useRef<Song | null>(null);
  const wasPlayingBeforePrayerRef = useRef(false);
  const prayerPauseActiveRef = useRef(false);

  const isPrayerPlaybackBlocked = () => {
    return showPrayerModal || prayerPauseActiveRef.current || prayerTimeService.getCurrentState().isPrayerTime;
  };

  useEffect(() => {
    currentSongIndexRef.current = currentSongIndex;
  }, [currentSongIndex]);

  useEffect(() => {
    currentSongRef.current = currentSong;
  }, [currentSong]);

  useEffect(() => {
    const handleUiSettingsUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{ settings?: UiSettings }>;
      if (customEvent.detail?.settings) {
        setUiSettings(customEvent.detail.settings);
      }
    };

    window.addEventListener('uiSettingsUpdated', handleUiSettingsUpdated);
    return () => {
      window.removeEventListener('uiSettingsUpdated', handleUiSettingsUpdated);
    };
  }, []);

  // 🛡️ GÜVENLE currentSongIndex değiştiğinde currentSong'u güncelle
  useEffect(() => {
    if (enhancedPlaylist.songs && 
        enhancedPlaylist.songs.length > 0 && 
        currentSongIndex >= 0 && 
        currentSongIndex < enhancedPlaylist.songs.length &&
        enhancedPlaylist.songs[currentSongIndex]) {
      setCurrentSong(enhancedPlaylist.songs[currentSongIndex]);
    } else {
      // Geçersiz index durumunda ilk şarkıya dön
      if (enhancedPlaylist.songs && enhancedPlaylist.songs.length > 0) {
        setCurrentSongIndex(0);
        setCurrentSong(enhancedPlaylist.songs[0]);
      }
    }
  }, [currentSongIndex]); // ✅ ÇÖZÜM: enhancedPlaylist.songs.length dependency KALDIRILDI - anons değişiminde tetiklenmesin
  
  // currentSong değişikliklerini takip et
  const prevCurrentSongRef = useRef<Song | null | undefined>();
  useEffect(() => {
    if (prevCurrentSongRef.current?._id !== currentSong?._id) {
      prevCurrentSongRef.current = currentSong;
    }
  }, [currentSong, currentSongIndex]);

  // Wake Lock API - Ekranın kapanmasını önle (müzik çalarken)
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    const requestWakeLock = async () => {
      if ('wakeLock' in navigator && isPlaying) {
        try {
          wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
        } catch (err) {
          // Wake Lock alınamadı - sessizce devam et
        }
      }
    };

    const releaseWakeLock = async () => {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };

    if (isPlaying) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isPlaying) {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      releaseWakeLock();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPlaying]);

  // Media Session API - Kilit ekranında kontrol ve arka plan çalma
  useEffect(() => {
    if ('mediaSession' in navigator && currentSong) {
      const artworkUrl = currentSong.artwork || currentSong.cdnUrl || playlist?.artwork;
      const fullArtworkUrl = artworkUrl
        ? (artworkUrl.startsWith('http') ? artworkUrl : `https://${artworkUrl}`)
        : null;

      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentSong.title || currentSong.name || 'Bilinmeyen Şarkı',
        artist: currentSong.artist || 'Bilinmeyen Sanatçı',
        album: playlist?.name || 'veeq',
        artwork: fullArtworkUrl ? [
          { src: fullArtworkUrl, sizes: '512x512', type: 'image/jpeg' }
        ] : []
      });

      navigator.mediaSession.setActionHandler('play', () => {
        const mediaAudio = audioRef.current;
        if (mediaAudio && !isPrayerPlaybackBlocked()) {
          const attemptId = ++playAttemptRef.current;
          mediaAudio.play().then(() => {
            if (playAttemptRef.current !== attemptId || audioRef.current !== mediaAudio) {
              return;
            }
            setIsPlaying(true);
          }).catch(error => {
            if (!isPlayInterruptionError(error) && error.name !== 'NotAllowedError') {
              console.error('Media session oynatma hatasÄ±:', error);
            }
          });
        }
      });

      navigator.mediaSession.setActionHandler('pause', () => {
        if (audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
      });

      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';

      // Position state - Kilit ekranında progress bar için
      if (duration > 0 && 'setPositionState' in navigator.mediaSession) {
        try {
          navigator.mediaSession.setPositionState({
            duration: duration,
            playbackRate: 1,
            position: Math.min(currentTime, duration)
          });
        } catch (e) {
          // Position state ayarlanamadı - sessizce devam et
        }
      }
    }
  }, [currentSong, isPlaying, playlist, currentTime, duration, showPrayerModal]);

  // Browser tab title güncelle
  useEffect(() => {
    // PWA modunda mı kontrol et
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    
    if (currentSong) {
      if (isPWA) {
        // PWA'da sadece şarkı/anons adı (duplicate önlemek için)
        document.title = currentSong.isAnnouncement 
          ? currentSong.title 
          : (currentSong.title || currentSong.name);
      } else {
        // Browser'da tam format
        if (currentSong.isAnnouncement) {
          document.title = `${currentSong.title} | Veeq Web Player`;
        } else {
          document.title = `${currentSong.title || currentSong.name} | Veeq Web Player`;
        }
      }
    } else {
      // Şarkı yoksa
      document.title = isPWA ? 'Veeq Web Player' : 'Veeq Web Player';
    }
  }, [currentSong]);

  // Anons aktif mi kontrol et
  const isAnnouncementActive = useCallback((announcement: Announcement) => {
    const now = new Date();
    const startDate = new Date(announcement.startDate);
    const endDate = new Date(announcement.endDate);
    const activeDays = Array.isArray(announcement.activeDays) ? announcement.activeDays : [];
    
    if (now < startDate || now > endDate) {
      return false;
    }
    
    if (activeDays.length > 0 && !activeDays.includes(now.getDay())) {
      return false;
    }

    return true;
  }, []);

  const buildPlaylistForPlayback = useCallback((
    sourcePlaylist: Playlist,
    preserveSongOrder = true,
    sourceAnnouncements = announcementsRef.current
  ) => {
    return buildEnterprisePlaylist<Song, Playlist, Announcement>(sourcePlaylist, {
      // Song-based announcements are injected at runtime when a song actually completes.
      // Keeping them out of the static playlist prevents duplicate back-to-back announcements.
      announcements: sourceAnnouncements.filter(announcement => announcement.scheduleType !== 'songs'),
      counters: announcementCountersRef.current,
      sameIntervalRotation: sameIntervalRotationRef.current,
      audioRotation: announcementAudioRotationRef.current,
      isAnnouncementActive,
      preserveSongOrder
    });
  }, [isAnnouncementActive]);

  const getPlaybackQueue = useCallback(() => {
    if (!playbackQueueRef.current) {
      playbackQueueRef.current = new PlaybackQueueEngine();
    }

    return playbackQueueRef.current;
  }, []);

  const getTrackStableId = useCallback((track?: Song | null) => {
    return track?._id || track?.cdnUrl || track?.filePath || null;
  }, []);

  const isTrackPlayable = useCallback((track?: Song | null) => {
    const trackId = getTrackStableId(track);
    return Boolean(
      track &&
      trackId &&
      !failedTrackIdsRef.current.has(trackId) &&
      resolveSongUrl(track, playlist.baseUrl)
    );
  }, [getTrackStableId, playlist.baseUrl]);

  const markTrackAsFailed = useCallback((track?: Song | null) => {
    const trackId = getTrackStableId(track);
    if (trackId) {
      failedTrackIdsRef.current.add(trackId);
    }
  }, [getTrackStableId]);

  const reportNoPlayableTracks = useCallback((sourcePlaylist?: Playlist | null, reason = 'no_playable_tracks') => {
    websocketService.sendPlaylistPlaybackBlocked(sourcePlaylist || enhancedPlaylistRef.current, reason);
  }, []);

  const selectQueuedSong = useCallback((
    sourcePlaylist: Playlist,
    direction: 'next' | 'prev' = 'next',
    startIndex = currentSongIndexRef.current
  ) => {
    const selection = getPlaybackQueue().selectNext<Song>({
      playlist: sourcePlaylist,
      currentIndex: startIndex,
      currentTrackId: getTrackStableId(currentSongRef.current),
      direction,
      isTrackPlayable
    });

    if (selection?.track) {
      return selection;
    }

    const fallbackIndex = sourcePlaylist.songs.findIndex(song => isTrackPlayable(song));
    if (fallbackIndex >= 0) {
      return {
        index: fallbackIndex,
        track: sourcePlaylist.songs[fallbackIndex],
        reason: 'fallback'
      };
    }

    return null;
  }, [getPlaybackQueue, getTrackStableId, isTrackPlayable]);

  const activatePlaylistForPlayback = useCallback((
    sourcePlaylist: Playlist,
    preserveSongOrder = true
  ) => {
    failedTrackIdsRef.current.clear();
    const playablePlaylist = buildPlaylistForPlayback(sourcePlaylist, preserveSongOrder);
    const selection = selectQueuedSong(playablePlaylist, 'next', -1);

    if (!selection?.track) {
      reportNoPlayableTracks(sourcePlaylist);
      return enhancedPlaylistRef.current;
    }

    setEnhancedPlaylist(playablePlaylist);
    enhancedPlaylistRef.current = playablePlaylist;
    setCurrentSongIndex(selection.index);
    setCurrentSong(selection.track);

    return playablePlaylist;
  }, [buildPlaylistForPlayback, reportNoPlayableTracks, selectQueuedSong]);

  const formatLocalDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatLocalMinute = (date: Date) => {
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    return `${hour}:${minute}`;
  };

  const normalizeAnnouncementTime = (time: string) => {
    const [hour, minute] = time.split(':');
    if (!hour || !minute) return time;
    return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  };

  const persistMinuteAnnouncementLastPlay = () => {
    localStateStore.setJson('announcementMinuteLastPlayTimes', minuteAnnouncementLastPlayRef.current);
  };

  const persistProcessedSpecificAnnouncements = () => {
    localStateStore.setJson(
      'processedSpecificAnnouncements',
      Array.from(processedSpecificAnnouncementsRef.current)
    );
  };

  const getAnnouncementAudioFiles = useCallback((announcement?: Announcement | null) => {
    if (Array.isArray(announcement?.audioFiles)) {
      return announcement.audioFiles;
    }

    if (Array.isArray(announcement?.audioFile)) {
      return announcement.audioFile;
    }

    return [];
  }, []);

  const selectAnnouncementAudioFile = useCallback((
    announcement: Announcement,
    options: { commitRotation?: boolean } = {}
  ) => {
    const audioFiles = getAnnouncementAudioFiles(announcement);
    const approvedAudioFiles = audioFiles.filter(audioFile =>
      audioFile?.url && (audioFile.status === 'approved' || !audioFile.status)
    );

    if (approvedAudioFiles.length === 0) {
      return null;
    }

    const currentIndex = announcementAudioRotationRef.current[announcement._id];
    const audioIndex = currentIndex === undefined
      ? 0
      : (currentIndex + 1) % approvedAudioFiles.length;

    if (options.commitRotation) {
      announcementAudioRotationRef.current[announcement._id] = audioIndex;
    }

    return {
      audioFile: approvedAudioFiles[audioIndex],
      audioIndex
    };
  }, [getAnnouncementAudioFiles]);

  const createTimedAnnouncementTrack = useCallback((
    announcement: Announcement,
    scheduleType: AnnouncementScheduleType,
    scheduledAnnouncementKey: string
  ): Song | null => {
    const audioSelection = selectAnnouncementAudioFile(announcement, { commitRotation: true });
    if (!audioSelection) {
      return null;
    }

    const { audioFile: selectedAudioFile, audioIndex } = audioSelection;

    return {
      _id: `announcement_${scheduleType}_${announcement._id}_${audioIndex}_${Date.now()}`,
      title: announcement.title || 'Anons',
      name: announcement.title || 'Anons',
      artist: announcement.content?.substring(0, 30) || 'Anons',
      filePath: selectedAudioFile.url,
      cdnUrl: selectedAudioFile.url,
      duration: 30,
      isAnnouncement: true,
      announcementId: announcement._id,
      audioFileId: selectedAudioFile.id || selectedAudioFile._id || selectedAudioFile.url,
      announcementScheduleType: scheduleType,
      scheduledAnnouncementKey
    };
  }, [selectAnnouncementAudioFile]);

  const queueTimedAnnouncement = useCallback((
    announcement: Announcement,
    scheduleType: AnnouncementScheduleType,
    scheduledAnnouncementKey: string
  ) => {
    const isAlreadyQueued = timedAnnouncementQueueRef.current.some(track =>
      track.scheduledAnnouncementKey === scheduledAnnouncementKey
    );
    const isCurrentlyPlaying = currentSongRef.current?.scheduledAnnouncementKey === scheduledAnnouncementKey;

    if (isAlreadyQueued || isCurrentlyPlaying) {
      return false;
    }

    const announcementTrack = createTimedAnnouncementTrack(announcement, scheduleType, scheduledAnnouncementKey);
    if (!announcementTrack) {
      return false;
    }

    timedAnnouncementQueueRef.current.push(announcementTrack);
    return true;
  }, [createTimedAnnouncementTrack]);

  const getActiveSongBasedAnnouncements = useCallback(() => {
    return (announcementsRef.current || []).filter(announcement => {
      const interval = Number(announcement.songInterval);
      const audioFiles = getAnnouncementAudioFiles(announcement);
      const hasPlayableFile = audioFiles.some(audioFile =>
        audioFile?.url && (audioFile.status === 'approved' || !audioFile.status)
      );

      return (
        announcement.scheduleType === 'songs' &&
        Number.isFinite(interval) &&
        interval >= 1 &&
        hasPlayableFile &&
        isAnnouncementActive(announcement)
      );
    });
  }, [getAnnouncementAudioFiles, isAnnouncementActive]);

  const selectDueSongAnnouncement = useCallback((
    dueAnnouncements: Announcement[],
    options: { commitRotation?: boolean } = {}
  ) => {
    if (dueAnnouncements.length === 0) return null;
    if (dueAnnouncements.length === 1) return dueAnnouncements[0];

    const maxInterval = Math.max(...dueAnnouncements.map(announcement => Number(announcement.songInterval) || 1));
    const maxIntervalAnnouncements = dueAnnouncements.filter(
      announcement => Number(announcement.songInterval) === maxInterval
    );

    if (maxIntervalAnnouncements.length === 1) {
      return maxIntervalAnnouncements[0];
    }

    const rotationKey = `songs_interval_${maxInterval}`;
    const currentIndex = sameIntervalRotationRef.current[rotationKey];
    const rotationIndex = currentIndex === undefined
      ? 0
      : (currentIndex + 1) % maxIntervalAnnouncements.length;

    if (options.commitRotation) {
      sameIntervalRotationRef.current[rotationKey] = rotationIndex;
    }

    return maxIntervalAnnouncements[rotationIndex];
  }, []);

  const markSongCompletedForAnnouncements = useCallback((track?: Song | null) => {
    if (!track || track.isAnnouncement) {
      return false;
    }

    const trackId = getTrackStableId(track);
    const now = Date.now();
    const lastCompletion = lastSongAnnouncementCompletionRef.current;
    if (
      trackId &&
      lastCompletion.trackId === trackId &&
      now - lastCompletion.timestamp < 30000
    ) {
      return false;
    }

    lastSongAnnouncementCompletionRef.current = { trackId, timestamp: now };

    const activeAnnouncements = getActiveSongBasedAnnouncements();
    if (activeAnnouncements.length === 0) {
      return false;
    }

    activeAnnouncements.forEach(announcement => {
      const interval = Number(announcement.songInterval);
      if (!Number.isFinite(interval) || interval < 1) return;
      announcementCountersRef.current[announcement._id] =
        (announcementCountersRef.current[announcement._id] || 0) + 1;
    });

    const dueAnnouncements = activeAnnouncements.filter(announcement => {
      const interval = Number(announcement.songInterval);
      return (
        Number.isFinite(interval) &&
        interval > 0 &&
        (announcementCountersRef.current[announcement._id] || 0) >= interval
      );
    });

    const selectedAnnouncement = selectDueSongAnnouncement(dueAnnouncements, { commitRotation: true });
    if (!selectedAnnouncement) {
      return false;
    }

    const todayKey = formatLocalDateKey(new Date());
    const scheduledAnnouncementKey = `${selectedAnnouncement._id}:songs:${todayKey}:${now}`;
    const queued = queueTimedAnnouncement(selectedAnnouncement, 'songs', scheduledAnnouncementKey);
    if (queued) {
      announcementCountersRef.current[selectedAnnouncement._id] = 0;
    }

    return queued;
  }, [getActiveSongBasedAnnouncements, getTrackStableId, selectDueSongAnnouncement, queueTimedAnnouncement]);

  const playQueuedTimedAnnouncement = useCallback(() => {
    const nextAnnouncement = timedAnnouncementQueueRef.current.shift();
    if (!nextAnnouncement) {
      return false;
    }

    currentlyPlayingTrackRef.current = null;
    setCurrentSong(nextAnnouncement);
    return true;
  }, []);

  const playImmediateSpecificAnnouncement = useCallback((
    announcement: Announcement,
    scheduledAnnouncementKey: string
  ) => {
    const isAlreadyQueued = timedAnnouncementQueueRef.current.some(track =>
      track.scheduledAnnouncementKey === scheduledAnnouncementKey
    );
    const isCurrentlyPlaying = currentSongRef.current?.scheduledAnnouncementKey === scheduledAnnouncementKey;

    if (isAlreadyQueued || isCurrentlyPlaying) {
      return false;
    }

    const announcementTrack = createTimedAnnouncementTrack(announcement, 'specific', scheduledAnnouncementKey);
    if (!announcementTrack) {
      return false;
    }

    const audio = audioRef.current;
    const currentSongSnapshot = currentSongRef.current;

    if (currentSongSnapshot) {
      interruptedPlaybackStackRef.current.push({
        song: currentSongSnapshot,
        index: currentSongIndexRef.current,
        time: audio?.currentTime || 0,
        wasPlaying: audio ? !audio.paused : isPlaying
      });
    }

    forceTimedAnnouncementPlaybackRef.current = scheduledAnnouncementKey;
    currentlyPlayingTrackRef.current = null;
    audio?.pause();
    setCurrentSong(announcementTrack);
    return true;
  }, [createTimedAnnouncementTrack, isPlaying]);

  const restoreInterruptedPlayback = useCallback(() => {
    const interruptedPlayback = interruptedPlaybackStackRef.current.pop();
    if (!interruptedPlayback) {
      return false;
    }

    const currentSongs = enhancedPlaylistRef.current?.songs || [];
    const restoredIndex = currentSongs.findIndex(song => song._id === interruptedPlayback.song._id);
    const targetIndex = restoredIndex >= 0 ? restoredIndex : interruptedPlayback.index;

    pendingResumeRef.current = {
      songId: interruptedPlayback.song._id,
      time: interruptedPlayback.time,
      shouldPlay: interruptedPlayback.wasPlaying
    };

    currentlyPlayingTrackRef.current = null;
    setCurrentSongIndex(targetIndex);
    setCurrentSong(interruptedPlayback.song);
    return true;
  }, []);

  const pruneTimedAnnouncementQueue = useCallback((announcementList: Announcement[]) => {
    const activeAnnouncementIds = new Set(
      announcementList
        .filter(announcement =>
          isAnnouncementActive(announcement)
        )
        .map(announcement => announcement._id)
    );

    timedAnnouncementQueueRef.current = timedAnnouncementQueueRef.current.filter(track =>
      !track.announcementScheduleType ||
      (track.announcementId && activeAnnouncementIds.has(track.announcementId))
    );
  }, [isAnnouncementActive]);

  const getSongAnnouncementSignature = useCallback((announcementList: Announcement[]) => {
    return (announcementList || [])
      .filter(announcement => announcement?.scheduleType === 'songs' && isAnnouncementActive(announcement))
      .map(announcement => [
        announcement._id,
        Number(announcement.songInterval) || '',
        announcement.startDate || '',
        announcement.endDate || '',
        Array.isArray(announcement.activeDays) ? announcement.activeDays.join(',') : '',
        getAnnouncementAudioFiles(announcement)
          .map(file => [
            file?._id || file?.id || file?.url || file?.fileName || '',
            file?.status || ''
          ].join('@'))
          .join('|')
      ].join(':'))
      .sort()
      .join('||');
  }, [getAnnouncementAudioFiles, isAnnouncementActive]);

  const resetSongAnnouncementRuntimeStateIfNeeded = useCallback((announcementList: Announcement[]) => {
    const signature = getSongAnnouncementSignature(announcementList);
    if (signature === songAnnouncementSignatureRef.current) {
      return false;
    }

    songAnnouncementSignatureRef.current = signature;

    const activeSongAnnouncementIds = new Set(
      (announcementList || [])
        .filter(announcement => announcement?.scheduleType === 'songs' && isAnnouncementActive(announcement))
        .map(announcement => announcement._id)
    );

    const nextCounters: Record<string, number> = {};
    activeSongAnnouncementIds.forEach(announcementId => {
      nextCounters[announcementId] = 0;
    });

    announcementCountersRef.current = nextCounters;
    sameIntervalRotationRef.current = {};
    lastSongAnnouncementCompletionRef.current = { trackId: null, timestamp: 0 };
    timedAnnouncementQueueRef.current = timedAnnouncementQueueRef.current.filter(
      track => track.announcementScheduleType !== 'songs'
    );

    return true;
  }, [getSongAnnouncementSignature, isAnnouncementActive]);

  // IndexedDB artwork cache
  const openArtworkDB = async () => {
    return new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open('ArtworkCache', 1);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('artworks')) {
          db.createObjectStore('artworks', { keyPath: 'url' });
        }
      };
    });
  };

  const getCachedArtwork = async (url: string): Promise<string | null> => {
    try {
      const db = await openArtworkDB();
      const transaction = db.transaction(['artworks'], 'readonly');
      const store = transaction.objectStore('artworks');
      
      return new Promise((resolve) => {
        const request = store.get(url);
        request.onsuccess = () => {
          const result = request.result?.blob || null;
          console.log('IndexedDB cache kontrolü:', url, result ? 'BULUNDU' : 'BOŞ');
          resolve(result);
        };
        request.onerror = () => {
          console.log('IndexedDB cache hatası:', url);
          resolve(null);
        };
      });
    } catch (error) {
      console.log('IndexedDB açma hatası:', error);
      return null;
    }
  };

  const cacheArtwork = async (url: string, blob: string) => {
    try {
      const db = await openArtworkDB();
      const transaction = db.transaction(['artworks'], 'readwrite');
      const store = transaction.objectStore('artworks');
      
      store.put({ url, blob, timestamp: Date.now() });
    } catch (error) {
    }
  };


  // STABLE CORS-free color extraction - NO MORE crossOrigin issues!
  const extractColorsFromArtwork = async (artworkUrl: string) => {
    if (!artworkUrl || !ArtworkColorExtractor.isValidImageUrl(artworkUrl)) {
      console.warn('⚠️ Invalid artwork URL:', artworkUrl);
      return;
    }

    try {
      setIsColorExtracting(true);
      console.log('🎨 Starting stable color extraction for:', artworkUrl);
      
      // Use stable fetch + blob approach (NO CORS issues!)
      const result = await ArtworkColorExtractor.extractColors(artworkUrl);
      
      setColorResult(result);
      
      // Also update old gradient format for compatibility
      const { colors } = result;
      const dominantHex = colors.dominant;
      
      // Convert hex to RGB for old format compatibility
      const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 55, g: 65, b: 81 };
      };
      
      const dominantRgb = hexToRgb(dominantHex);
      
      const darkerColor = [
        Math.max(0, dominantRgb.r - 50),
        Math.max(0, dominantRgb.g - 50),
        Math.max(0, dominantRgb.b - 50)
      ];
      
      const lighterColor = [
        Math.min(255, dominantRgb.r + 20),
        Math.min(255, dominantRgb.g + 20),
        Math.min(255, dominantRgb.b + 20)
      ];
      
      setGradientColors({
        primary: `${darkerColor[0]}, ${darkerColor[1]}, ${darkerColor[2]}`,
        secondary: `${dominantRgb.r}, ${dominantRgb.g}, ${dominantRgb.b}`,
        tertiary: `${lighterColor[0]}, ${lighterColor[1]}, ${lighterColor[2]}`,
        // Darker opacity values to match rich background experience
        primaryOpacity: 0.94, // F0 in hex
        secondaryOpacity: 0.85, // D9 in hex
        tertiaryOpacity: 0.70, // B3 in hex
        isDark: colors.isDark
      });
      
      console.log('✅ Stable color extraction successful!', result);
      
    } catch (error) {
      console.error('❌ Stable color extraction failed:', error);
      // Use fallback colors with darker opacity values
      setGradientColors({
        primary: '25, 25, 45',
        secondary: '60, 60, 90', 
        tertiary: '100, 100, 140',
        primaryOpacity: 0.94, // F0 in hex
        secondaryOpacity: 0.85, // D9 in hex
        tertiaryOpacity: 0.70, // B3 in hex
        isDark: true
      });
    } finally {
      setIsColorExtracting(false);
    }
  };

  // STABLE CORS-free artwork caching - NO MORE crossOrigin issues!
  const downloadAndCacheArtwork = async (artworkUrl: string): Promise<string | null> => {
    if (!artworkUrl || !ArtworkColorExtractor.isValidImageUrl(artworkUrl)) {
      console.warn('⚠️ Invalid artwork URL for caching:', artworkUrl);
      return null;
    }

    try {
      console.log('💾 Starting stable artwork caching for:', artworkUrl);
      
      // Step 1: Fetch with stable CORS approach
      const response = await fetch(artworkUrl, {
        mode: 'cors',
        cache: 'no-cache', // Force fresh request with Origin header
        headers: {
          'Origin': window.location.origin // Explicitly set Origin header
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Step 2: Load blob URL in image element (NO CORS issues!)
      return new Promise((resolve) => {
        const img = new Image();
        
        img.onload = async () => {
          try {
            // Canvas approach for base64 conversion
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              throw new Error('Canvas context not available');
            }
            
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            
            const base64 = canvas.toDataURL('image/jpeg', 0.8);
            
            // IndexedDB cache
            await cacheArtwork(artworkUrl, base64);
            console.log('✅ Stable artwork caching successful!');
            
            // Cleanup
            URL.revokeObjectURL(blobUrl);
            resolve(base64);
            
          } catch (error) {
            console.error('❌ Canvas processing failed:', error);
            URL.revokeObjectURL(blobUrl);
            resolve(null);
          }
        };
        
        img.onerror = (error) => {
          console.error('❌ Blob image load failed:', error);
          URL.revokeObjectURL(blobUrl);
          resolve(null);
        };
        
        // Load from stable blob URL (NO CORS issues!)
        img.src = blobUrl;
      });
      
    } catch (error) {
      console.error('❌ Stable artwork caching failed:', error);
      return null;
    }
  };

  // Playlist artwork'ten renkleri çıkar - Desktop gibi direkt
  useEffect(() => {
    if (enhancedPlaylist.artwork) {
      extractColorsFromArtwork(enhancedPlaylist.artwork);
    }
  }, [enhancedPlaylist.artwork]);

  // İlk yüklenmede anonsları kontrol et
  useEffect(() => {
    try {
      const parsedAnnouncements = localStateStore.getJson<Announcement[]>('announcements', null);
      if (parsedAnnouncements) {
        if (Array.isArray(parsedAnnouncements)) {
          setAnnouncements(parsedAnnouncements);
          announcementsRef.current = parsedAnnouncements;
          resetSongAnnouncementRuntimeStateIfNeeded(parsedAnnouncements);
        } else {
          localStateStore.remove('announcements');
        }
      }
    } catch {
      localStateStore.remove('announcements');
    }

    try {
      const parsedMinuteLastPlay = localStateStore.getJson<Record<string, number>>('announcementMinuteLastPlayTimes', null);
      if (parsedMinuteLastPlay) {
        if (parsedMinuteLastPlay && typeof parsedMinuteLastPlay === 'object' && !Array.isArray(parsedMinuteLastPlay)) {
          minuteAnnouncementLastPlayRef.current = parsedMinuteLastPlay;
        } else {
          localStateStore.remove('announcementMinuteLastPlayTimes');
        }
      }
    } catch {
      localStateStore.remove('announcementMinuteLastPlayTimes');
    }

    try {
      const parsedSpecificKeys = localStateStore.getJson<string[]>('processedSpecificAnnouncements', null);
      if (parsedSpecificKeys) {
        if (Array.isArray(parsedSpecificKeys)) {
          processedSpecificAnnouncementsRef.current = new Set(parsedSpecificKeys);
        } else {
          localStateStore.remove('processedSpecificAnnouncements');
        }
      }
    } catch {
      localStateStore.remove('processedSpecificAnnouncements');
    }

    setAnnouncementsLoaded(true);
  }, [resetSongAnnouncementRuntimeStateIfNeeded]);

  // Playlist'i karıştırma yapmadan anonslarla güncelleme (anons değişimlerinde)
  useEffect(() => {
    if (!announcementsLoaded) {
      return;
    }

    const processTimedAnnouncements = () => {
      if (isSpecialDayActiveRef.current) {
        return;
      }

      const now = new Date();
      const todayKey = formatLocalDateKey(now);
      const currentMinute = formatLocalMinute(now);
      const allAnnouncements = announcementsRef.current || [];

      allAnnouncements.forEach(announcement => {
        if (!isAnnouncementActive(announcement)) {
          return;
        }

        if (
          announcement.scheduleType === 'minutes' &&
          typeof announcement.minuteInterval === 'number' &&
          announcement.minuteInterval > 0
        ) {
          const intervalMs = announcement.minuteInterval * 60000;
          const activationTime = new Date(announcement.startDate).getTime();
          const lastPlayTime = minuteAnnouncementLastPlayRef.current[announcement._id] || activationTime;

          if (now.getTime() - lastPlayTime >= intervalMs) {
            const intervalSlot = Math.floor(now.getTime() / intervalMs);
            const scheduledKey = `${announcement._id}:minutes:${intervalSlot}`;

            queueTimedAnnouncement(announcement, 'minutes', scheduledKey);
            minuteAnnouncementLastPlayRef.current[announcement._id] = now.getTime();
            persistMinuteAnnouncementLastPlay();
          }
        }

        if (
          announcement.scheduleType === 'specific' &&
          Array.isArray(announcement.specificTimes) &&
          announcement.specificTimes.length > 0
        ) {
          const normalizedTimes = announcement.specificTimes.map(normalizeAnnouncementTime);
          if (!normalizedTimes.includes(currentMinute)) {
            return;
          }

          const scheduledKey = `${announcement._id}:specific:${todayKey}:${currentMinute}`;
          if (processedSpecificAnnouncementsRef.current.has(scheduledKey)) {
            return;
          }

          processedSpecificAnnouncementsRef.current.add(scheduledKey);
          persistProcessedSpecificAnnouncements();
          playImmediateSpecificAnnouncement(announcement, scheduledKey);
        }
      });
    };

    processTimedAnnouncements();
    const timer = window.setInterval(processTimedAnnouncements, 15000);

    return () => window.clearInterval(timer);
  }, [announcementsLoaded, isAnnouncementActive, playImmediateSpecificAnnouncement, queueTimedAnnouncement]);

  const updatePlaylistWithoutShuffle = useCallback(() => {
    if (isSpecialDayActiveRef.current) {
      return;
    }

    const baseSongs = enhancedPlaylistRef.current.songs.filter(song => !song.isAnnouncement);
    const updatedPlaylist = buildPlaylistForPlayback({
      ...enhancedPlaylistRef.current,
      songs: baseSongs
    }, true);

    enhancedPlaylistRef.current = updatedPlaylist;
    setEnhancedPlaylist(updatedPlaylist);
  }, [buildPlaylistForPlayback]);

  const applyAnnouncementsRuntimeUpdate = useCallback((
    nextAnnouncements: Announcement[],
    options: { markLoaded?: boolean } = {}
  ) => {
    const safeAnnouncements = Array.isArray(nextAnnouncements) ? nextAnnouncements : [];

    setAnnouncements(safeAnnouncements);
    announcementsRef.current = safeAnnouncements;
    pruneTimedAnnouncementQueue(safeAnnouncements);
    resetSongAnnouncementRuntimeStateIfNeeded(safeAnnouncements);

    if (options.markLoaded) {
      setAnnouncementsLoaded(true);
    }

    updatePlaylistWithoutShuffle();
  }, [pruneTimedAnnouncementQueue, resetSongAnnouncementRuntimeStateIfNeeded, updatePlaylistWithoutShuffle]);

  // Playlist ilk geldiğinde karıştır (tek seferlik) - anonslar yüklendikten sonra
  useEffect(() => {
    if (isSpecialDayActiveRef.current) {
      return;
    }

    // ✅ ÇÖZÜM: Hem playlist hem de anonslar hazır olana kadar bekle
    if (playlist.songs && playlist.songs.length > 0 && announcementsLoaded) {
      activatePlaylistForPlayback(playlist, true);
    }
  }, [playlistSignature, announcementsLoaded, activatePlaylistForPlayback]); // Playlist icerigi degisirse ayni ID/adet olsa da yeniden kurulur

  // ✅ ÇÖZÜM: Anons event'leri ile tetiklenen güncelleme - otomatik useEffect değil

  // ✅ SAĞLAM & GÜVENLİ Anons Silme - Immutable State
  const removeAnnouncementFromPlaylist = useCallback((announcementId: string) => {
    if (!announcementId) return;
    
    setEnhancedPlaylist(currentPlaylist => {
      try {
        // Immutable filtering - güvenli silme
        const filteredSongs = currentPlaylist.songs.filter(song => 
          song.announcementId !== announcementId
        );
        
        // Değişiklik var mı kontrol et
        if (filteredSongs.length === currentPlaylist.songs.length) {
          return currentPlaylist; // Değişiklik yoksa aynı reference döndür
        }
        
        // Yeni playlist oluştur
        const newPlaylist = {
          ...currentPlaylist,
          songs: filteredSongs
        };
        
        // Ref'i güncelle
        enhancedPlaylistRef.current = newPlaylist;
        
        return newPlaylist;
        
      } catch (error) {
        console.error('Anons silme hatası:', error);
        return currentPlaylist; // Hata durumunda mevcut playlist'i koru
      }
    });
  }, []);


  // Tüm anons event'lerini dinle
  useEffect(() => {
    const handleAnnouncementReceived = (event: CustomEvent) => {
      const { allAnnouncements } = event.detail;
      applyAnnouncementsRuntimeUpdate(allAnnouncements);
    };

    const handleAnnouncementUpdated = (event: CustomEvent) => {
      const { allAnnouncements } = event.detail;
      applyAnnouncementsRuntimeUpdate(allAnnouncements);
    };

    const handleAnnouncementsListReceived = (event: CustomEvent) => {
      const { allAnnouncements } = event.detail;
      applyAnnouncementsRuntimeUpdate(allAnnouncements);
    };

    const handleAnnouncementDeleted = (event: CustomEvent) => {
      const { deletedId, allAnnouncements } = event.detail;
      applyAnnouncementsRuntimeUpdate(allAnnouncements);
      timedAnnouncementQueueRef.current = timedAnnouncementQueueRef.current.filter(track =>
        track.announcementId !== deletedId
      );
      
      removeAnnouncementFromPlaylist(deletedId);
    };

    const handleAnnouncementsFullSync = (event: CustomEvent) => {
      const { announcements } = event.detail;
      applyAnnouncementsRuntimeUpdate(announcements, { markLoaded: true });
    };
    
    // Event listener'ları ekle
    window.addEventListener('announcementReceived', handleAnnouncementReceived as EventListener);
    window.addEventListener('announcementUpdated', handleAnnouncementUpdated as EventListener);
    window.addEventListener('announcementsListReceived', handleAnnouncementsListReceived as EventListener);
    window.addEventListener('announcementDeleted', handleAnnouncementDeleted as EventListener);
    window.addEventListener('announcementsFullSync', handleAnnouncementsFullSync as EventListener);
    
    return () => {
      window.removeEventListener('announcementReceived', handleAnnouncementReceived as EventListener);
      window.removeEventListener('announcementUpdated', handleAnnouncementUpdated as EventListener);
      window.removeEventListener('announcementsListReceived', handleAnnouncementsListReceived as EventListener);
      window.removeEventListener('announcementDeleted', handleAnnouncementDeleted as EventListener);
      window.removeEventListener('announcementsFullSync', handleAnnouncementsFullSync as EventListener);
    };
  }, [applyAnnouncementsRuntimeUpdate, removeAnnouncementFromPlaylist]);

  // Ezan vakti event listener'ları
  useEffect(() => {
    // Component mount olduğunda mevcut ezan durumunu kontrol et
    const pauseForPrayer = () => {
      const audio = audioRef.current;
      if (!prayerPauseActiveRef.current) {
        wasPlayingBeforePrayerRef.current = Boolean(audio ? !audio.paused : isPlaying);
        prayerPauseActiveRef.current = true;
      }
      playbackEngineRef.current.cleanup(
        audioRef.current,
        crossfadeAudioRef.current,
        isMuted ? 0 : volume / 100
      );

      if (audio && !audio.paused) {
        suppressPauseEventRef.current = true;
        audio.pause();
        window.setTimeout(() => {
          suppressPauseEventRef.current = false;
        }, 250);
      }

      setIsPlaying(false);
    };

    const shouldResumeAfterPrayer = () => {
      return (
        wasPlayingBeforePrayerRef.current &&
        !isEmergencyStopped &&
        !isRemotePaused &&
        !showScheduleModal &&
        musicScheduleService.isPlayingAllowed()
      );
    };

    const currentState = prayerTimeService.getCurrentState();

    if (
      currentState.isPrayerTime &&
      currentState.activePrayerName &&
      currentState.prayerEndTime &&
      !prayerPauseActiveRef.current
    ) {
      // Ezan vakti aktifse modal'ı göster
      setPrayerTimeInfo({
        prayerName: currentState.activePrayerName,
        endTime: currentState.prayerEndTime
      });
      setShowPrayerModal(true);

      // Manager'a ezan durumunu bildir
      playbackStatusService.sendEzanStarted(currentState.activePrayerName);

      // Müzik çalıyorsa durdur
      pauseForPrayer();
    }
    
    const handlePrayerTimeStarted = (event: CustomEvent) => {
      const { prayerName, endTime } = event.detail;

      // Müziği durdur
      pauseForPrayer();

      // Manager'a ezan durumunu bildir
      playbackStatusService.sendEzanStarted(prayerName);

      // Modal'ı göster
      setPrayerTimeInfo({
        prayerName,
        endTime: new Date(endTime)
      });
      setShowPrayerModal(true);
    };

    const handlePrayerTimeEnded = (event: CustomEvent) => {

      // Modal'ı kapat
      setShowPrayerModal(false);
      setPrayerTimeInfo(null);

      // Manager'a ezan bittiğini bildir
      playbackStatusService.sendEzanEnded();

      // Müziği devam ettir
      const resumeAudio = audioRef.current;
      if (resumeAudio && !isPlaying && shouldResumeAfterPrayer()) {
        const attemptId = ++playAttemptRef.current;
        resumeAudio.play().then(() => {
          if (playAttemptRef.current !== attemptId || audioRef.current !== resumeAudio) {
            return;
          }
          setIsPlaying(true);
        }).catch(error => {
          if (
            playAttemptRef.current !== attemptId ||
            audioRef.current !== resumeAudio ||
            isPlayInterruptionError(error)
          ) {
            return;
          }

          if (!isPlayInterruptionError(error) && error.name !== 'NotAllowedError') {
            console.error('Müzik devam ettirme hatası:', error);
          }
        });
      }

      wasPlayingBeforePrayerRef.current = false;
      prayerPauseActiveRef.current = false;
    };
    
    window.addEventListener('prayerTimeStarted', handlePrayerTimeStarted as EventListener);
    window.addEventListener('prayerTimeEnded', handlePrayerTimeEnded as EventListener);
    
    return () => {
      window.removeEventListener('prayerTimeStarted', handlePrayerTimeStarted as EventListener);
      window.removeEventListener('prayerTimeEnded', handlePrayerTimeEnded as EventListener);
    };
  }, [
    isEmergencyStopped,
    isPlaying,
    isMuted,
    isRemotePaused,
    showScheduleModal,
    volume
  ]);

  // Certificate event listener'ı
  useEffect(() => {
    const handleCertificateReceived = (event: CustomEvent) => {

      if (event.detail && event.detail.data) {
        setCertificateData(event.detail.data);
      }
    };

    window.addEventListener('certificateReceived', handleCertificateReceived as EventListener);

    return () => {
      window.removeEventListener('certificateReceived', handleCertificateReceived as EventListener);
    };
  }, []);

  // ========================================
  // SCHEDULE EVENT LISTENERS (Desktop'tan adapt)
  // ========================================

  // Özel Gün Playlist event listener'ları
  useEffect(() => {
    const resolveRestorePlaylist = (): Playlist | null => {
      if (specialDayBackupPlaylistRef.current) {
        return specialDayBackupPlaylistRef.current;
      }

      const raw = localStateStore.getString('currentPlaylist');
      if (!raw) return null;

      try {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.songs)) {
          return parsed as Playlist;
        }
      } catch (error) {
        console.error('[Player] Restore playlist parse hatası:', error);
      }

      return null;
    };

    const handleSpecialDayPlaylistStarted = (event: CustomEvent) => {
      const specialPlaylist = event.detail?.playlist as Playlist | undefined;
      if (!specialPlaylist?.songs || specialPlaylist.songs.length === 0) {
        return;
      }

      if (!isSpecialDayActiveRef.current) {
        specialDayBackupPlaylistRef.current = enhancedPlaylistRef.current;
      }

      localStateStore.setString('playerIsPlaying', 'true');
      const playableSpecialPlaylist = activatePlaylistForPlayback(specialPlaylist, true);
      setIsSpecialDayActive(true);

      if (specialPlaylist.artwork) {
        extractColorsFromArtwork(specialPlaylist.artwork);
      }
    };

    const handleSpecialDayPlaylistEnded = () => {
      const restorePlaylist = resolveRestorePlaylist();
      setIsSpecialDayActive(false);

      if (restorePlaylist?.songs && restorePlaylist.songs.length > 0) {
        const playableRestorePlaylist = activatePlaylistForPlayback(restorePlaylist, true);

        if (restorePlaylist.artwork) {
          extractColorsFromArtwork(restorePlaylist.artwork);
        }
      }

      specialDayBackupPlaylistRef.current = null;
    };

    window.addEventListener('special-day-playlist-started', handleSpecialDayPlaylistStarted as EventListener);
    window.addEventListener('special-day-playlist-ended', handleSpecialDayPlaylistEnded as EventListener);

    // Event listener'lar hazır olduktan sonra anlık kontrol yap
    specialDayService.runCheckNow();

    return () => {
      window.removeEventListener('special-day-playlist-started', handleSpecialDayPlaylistStarted as EventListener);
      window.removeEventListener('special-day-playlist-ended', handleSpecialDayPlaylistEnded as EventListener);
    };
  }, [buildPlaylistForPlayback, activatePlaylistForPlayback]);

  // Schedule Playlist Aktivasyonu event listener'ı
  useEffect(() => {
    const handleSchedulePlaylistActivated = (event: CustomEvent) => {
      if (event.detail && event.detail.playlist) {
        const scheduledPlaylist = event.detail.playlist;

        if (isSpecialDayActiveRef.current) {
          specialDayBackupPlaylistRef.current = buildPlaylistForPlayback(scheduledPlaylist, true);
          return;
        }

        const playableScheduledPlaylist = activatePlaylistForPlayback(scheduledPlaylist, true);

        if (scheduledPlaylist.artwork) {
          extractColorsFromArtwork(scheduledPlaylist.artwork);
        }
      }
    };

    window.addEventListener('schedule-playlist-activated', handleSchedulePlaylistActivated as EventListener);

    return () => {
      window.removeEventListener('schedule-playlist-activated', handleSchedulePlaylistActivated as EventListener);
    };
  }, [buildPlaylistForPlayback, activatePlaylistForPlayback]);

  // Manuel Playlist'e Geçiş event listener'ı
  useEffect(() => {
    const handleSwitchToManualPlaylist = (event: CustomEvent) => {
      if (event.detail && event.detail.playlist) {
        const manualPlaylist = event.detail.playlist;

        if (isSpecialDayActiveRef.current) {
          specialDayBackupPlaylistRef.current = buildPlaylistForPlayback(manualPlaylist, true);
          return;
        }

        const playableManualPlaylist = activatePlaylistForPlayback(manualPlaylist, true);

        if (manualPlaylist.artwork) {
          extractColorsFromArtwork(manualPlaylist.artwork);
        }
      }
    };

    window.addEventListener('switch-to-manual-playlist', handleSwitchToManualPlaylist as EventListener);

    return () => {
      window.removeEventListener('switch-to-manual-playlist', handleSwitchToManualPlaylist as EventListener);
    };
  }, [buildPlaylistForPlayback, activatePlaylistForPlayback]);

  // Music Schedule (Planlı Çalma Saatleri) event listener'ı
  useEffect(() => {
    const handleMusicScheduleUpdated = (event: CustomEvent) => {
      if (event.detail) {
        const { action, schedule } = event.detail;

        if (action === 'set' && schedule) {
          setMusicSchedule(schedule);
        } else if (action === 'delete') {
          setMusicSchedule(null);
        }
      }
    };

    window.addEventListener('music-schedule-updated', handleMusicScheduleUpdated as EventListener);

    // İlk yüklenmede music schedule'ı kontrol et
    const currentMusicSchedule = musicScheduleService.getMusicSchedule();
    if (currentMusicSchedule) {
      setMusicSchedule(currentMusicSchedule);
    }

    return () => {
      window.removeEventListener('music-schedule-updated', handleMusicScheduleUpdated as EventListener);
    };
  }, []);

  // Music Schedule Kontrolü (Ezan vakti sistemi gibi)
  // Her 30 saniyede bir kontrol et
  useEffect(() => {
    const checkMusicSchedule = () => {
      if (!musicSchedule || !musicSchedule.enabled) {
        // Schedule kaldırıldı veya devre dışı - Modal kapat ve müziği başlat (Ezan sistemi gibi)
        if (scheduleState.active) {
          console.log('[Player] Schedule kaldırıldı - Modal kapatılıyor ve müzik başlatılıyor');
          setScheduleState({ active: false, message: '' });
          setShowScheduleModal(false);

          // Müziği devam ettir (Ezan sistemi gibi)
          if (audioRef.current && !isPlaying && !isPrayerPlaybackBlocked()) {
            audioRef.current.play().then(() => {
              setIsPlaying(true);
              console.log('[Player] Schedule kaldırıldı - Müzik başlatıldı');
            }).catch(error => {
              if (!isPlayInterruptionError(error) && error.name !== 'NotAllowedError') {
                console.error('[Player] Müzik başlatma hatası:', error);
              }
            });
          }
        }
        return;
      }

      const isAllowed = musicScheduleService.isPlayingAllowed();

      if (!isAllowed) {
        // Planlı çalma saatleri DIŞINDA - Müziği durdur ve modal göster
        const startTime = new Date(musicSchedule.startDateTime);
        const endTime = new Date(musicSchedule.endDateTime);
        const message = `Müzik çalma saatleri: ${startTime.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })} - ${endTime.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;

        // Modal'ı göster
        if (!scheduleState.active) {
          console.log('[Player] Planlı çalma dışı - Modal gösteriliyor');
          setScheduleState({ active: true, message });
          setShowScheduleModal(true);
        }

        // Müzik çalıyorsa DURDUR (Ezan sistemi gibi)
        if (audioRef.current && isPlaying) {
          console.log('[Player] Planlı çalma dışı - Müzik durduruluyor');
          audioRef.current.pause();
          setIsPlaying(false);
        }
      } else if (scheduleState.active) {
        // Planlı çalma saatleri İÇİNDE - Modal'ı kapat ve müziği devam ettir
        console.log('[Player] Planlı çalma içi - Modal kapatılıyor');
        setScheduleState({ active: false, message: '' });
        setShowScheduleModal(false);

        // Müziği devam ettir (Ezan sistemi gibi)
        if (audioRef.current && !isPlaying && !isPrayerPlaybackBlocked()) {
          audioRef.current.play().then(() => {
            setIsPlaying(true);
            console.log('[Player] Planlı çalma içi - Müzik devam ediyor');
          }).catch(error => {
            if (!isPlayInterruptionError(error) && error.name !== 'NotAllowedError') {
              console.error('[Player] Müzik devam ettirme hatası:', error);
            }
          });
        }
      }
    };

    // İlk kontrolü hemen yap
    checkMusicSchedule();

    // Her 30 saniyede bir kontrol et
    const intervalId = setInterval(checkMusicSchedule, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [musicSchedule, scheduleState.active, isPlaying, showPrayerModal]); // isPlaying eklendi (Ezan sistemi gibi)

  // Delete event listener'ı
  useEffect(() => {
    const handleDeleteReceived = (event: CustomEvent) => {
      
      const { target, targetId } = event.detail;
      
      if (target === 'announcement' && targetId) {
        
        // Anons listesinden sil
        setAnnouncements(currentAnnouncements => {
          const filteredAnnouncements = currentAnnouncements.filter(ann => ann._id !== targetId);
          return filteredAnnouncements;
        });
        
        // Playlist'ten anons şarkılarını sil
        setEnhancedPlaylist(currentPlaylist => {
          const filteredSongs = currentPlaylist.songs.filter(song => 
            !song.isAnnouncement || song.announcementId !== targetId
          );
          return {
            ...currentPlaylist,
            songs: filteredSongs
          };
        });
        
        // Anons event'ini fırlat (diğer componentler için)
        const announcementDeletedEvent = new CustomEvent('announcementDeleted', {
          detail: { 
            deletedId: targetId,
            allAnnouncements: announcements.filter(ann => ann._id !== targetId)
          }
        });
        window.dispatchEvent(announcementDeletedEvent);
        
        // Delete işlemi sonrası web player'ı reload et
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
      
      // Diğer delete türleri buraya eklenebilir (playlist, song, vs.)
      else if (target === 'playlist' && targetId) {
        // Playlist delete işlemi gerekirse buraya eklenebilir
        
        // Delete işlemi sonrası web player'ı reload et
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
      else if (target === 'song' && targetId) {
        // Şarkı delete işlemi gerekirse buraya eklenebilir
        
        // Delete işlemi sonrası web player'ı reload et
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
      else if (target === 'device' && targetId) {
        // Device reset websocketService tarafindan tek noktadan yonetilir.
        return;
      }
      else {
        
        // Bilinmeyen delete türü için de reload yap
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    };
    
    window.addEventListener('deleteReceived', handleDeleteReceived as EventListener);
    
    return () => {
      window.removeEventListener('deleteReceived', handleDeleteReceived as EventListener);
    };
  }, [announcements]);

  const playNext = useCallback(() => {
    // 🛡️ GÜVENLE KONTROL - Division by zero önlendi
    if (!enhancedPlaylist.songs || enhancedPlaylist.songs.length === 0) {
      return; // Boş playlist'te işlem yapma
    }
    
    const selection = selectQueuedSong(enhancedPlaylistRef.current, 'next', currentSongIndexRef.current);
    if (!selection?.track) {
      reportNoPlayableTracks(enhancedPlaylistRef.current);
      return;
    }

    const nextIndex = selection.index;
    const nextSong = selection.track;
    
    // Extra güvenlik: nextSong kontrolü
    currentlyPlayingTrackRef.current = null;
    setCurrentSongIndex(nextIndex);
    
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.play().then(() => {
          setIsPlaying(true);
          currentlyPlayingTrackRef.current = nextSong;
        }).catch(error => {
          if (!isPlayInterruptionError(error) && error.name !== 'NotAllowedError') {
            console.error('Sonraki şarkı çalma hatası:', error);
          }
        });
      }
    }, 100);
  }, [enhancedPlaylist.songs, reportNoPlayableTracks, selectQueuedSong]);

  const playPrevious = useCallback(() => {
    // 🛡️ GÜVENLE KONTROL - Array bounds error önlendi
    if (!enhancedPlaylist.songs || enhancedPlaylist.songs.length === 0) {
      return; // Boş playlist'te işlem yapma
    }
    
    const selection = selectQueuedSong(enhancedPlaylistRef.current, 'prev', currentSongIndexRef.current);
    if (!selection?.track || selection.index < 0 || selection.index >= enhancedPlaylist.songs.length) {
      reportNoPlayableTracks(enhancedPlaylistRef.current);
      return;
    }

    const prevIndex = selection.index;
    const prevSong = selection.track;
    
    // Extra güvenlik: prevSong kontrolü ve index sınırı
    currentlyPlayingTrackRef.current = null;
    setCurrentSongIndex(prevIndex);
    
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.play().then(() => {
          setIsPlaying(true);
          currentlyPlayingTrackRef.current = prevSong;
        }).catch(error => {
          if (!isPlayInterruptionError(error) && error.name !== 'NotAllowedError') {
            console.error('Önceki şarkı çalma hatası:', error);
          }
        });
      }
    }, 100);
  }, [enhancedPlaylist.songs, reportNoPlayableTracks, selectQueuedSong]);

  const getSongUrl = useCallback((song?: Song | null) => {
    return resolveSongUrl(song, playlist.baseUrl);
  }, [playlist.baseUrl]);

  const getTargetVolume = useCallback(() => {
    return isMuted ? 0 : Math.max(0, Math.min(1, volume / 100));
  }, [isMuted, volume]);

  const getEffectiveTrackDuration = useCallback((audio?: HTMLAudioElement | null, track?: Song | null): number => {
    const mediaDuration = Number(audio?.duration ?? 0);
    if (Number.isFinite(mediaDuration) && mediaDuration > 0) {
      return mediaDuration;
    }

    const trackDuration = Number(track?.duration ?? 0);
    if (Number.isFinite(trackDuration) && trackDuration > 0) {
      return trackDuration;
    }

    return 0;
  }, []);

  const isWebCrossfadeEnabled = useMemo(() => {
    return CONFIG.player.crossfadeEnabled &&
      (uiSettings.crossfade?.web?.enabled ?? uiSettings.crossfade?.enabled ?? true);
  }, [uiSettings]);

  const cleanupCrossfadeAudio = useCallback(() => {
    playbackEngineRef.current.cleanup(
      audioRef.current,
      crossfadeAudioRef.current,
      getTargetVolume()
    );
  }, [getTargetVolume]);

  const hasActiveSongAnnouncements = useCallback(() => {
    return getActiveSongBasedAnnouncements().length > 0;
  }, [getActiveSongBasedAnnouncements]);

  const startCrossfadeToNext = useCallback(() => {
    const primaryAudio = audioRef.current;
    const secondaryAudio = crossfadeAudioRef.current;
    const fadingOutSong = currentSongRef.current;
    const playlistSnapshot = enhancedPlaylistRef.current;
    const crossfadeDurationSeconds = CONFIG.player.crossfadeDuration;

    if (
      !isWebCrossfadeEnabled ||
      !primaryAudio ||
      !secondaryAudio ||
      !fadingOutSong ||
      fadingOutSong.isAnnouncement ||
      primaryAudio.paused ||
      getEffectiveTrackDuration(primaryAudio, fadingOutSong) <= crossfadeDurationSeconds + 4 ||
      showPrayerModal ||
      showScheduleModal ||
      isEmergencyStopped ||
      isRemotePaused ||
      isSpecialDayActiveRef.current ||
      timedAnnouncementQueueRef.current.length > 0 ||
      hasActiveSongAnnouncements()
    ) {
      return false;
    }

    return playbackEngineRef.current.startCrossfadeToNext({
      primaryAudio,
      secondaryAudio,
      playlist: playlistSnapshot,
      currentTrack: fadingOutSong,
      currentIndex: currentSongIndexRef.current,
      durationSeconds: crossfadeDurationSeconds,
      targetVolume: getTargetVolume(),
      getTrackUrl: getSongUrl,
      selectNext: (startIndex) => selectQueuedSong(playlistSnapshot, 'next', startIndex),
      isAnnouncement: (track) => Boolean(track?.isAnnouncement),
      isSameTrack: (left, right) => left?._id === right?._id,
      getTrackDuration: (track) => track?.duration || 0,
      onSuppressPrimaryPause: (suppress) => {
        suppressPauseEventRef.current = suppress;
      },
      onHandoff: ({ nextTrack, nextIndex, currentTime: handoffTime, duration: handoffDuration }) => {
        currentSongIndexRef.current = nextIndex;
        currentSongRef.current = nextTrack;
        currentlyPlayingTrackRef.current = nextTrack;
        setCurrentSongIndex(nextIndex);
        setCurrentSong(nextTrack);
        setCurrentTime(handoffTime || 0);
        setDuration(handoffDuration || nextTrack.duration || 0);
        setIsPlaying(true);
      },
      onPreviousTrackCompleted: (track) => {
        recordEnterpriseSongPlay(playlistSnapshot._id, track);
        markSongCompletedForAnnouncements(track);
      },
      onTrackFailed: markTrackAsFailed,
      onNoPlayableTracks: (sourcePlaylist, reason) => {
        setIsPlaying(false);
        reportNoPlayableTracks(sourcePlaylist, reason);
      },
      onLog: (message, error) => {
        if ((error as DOMException)?.name !== 'NotAllowedError') {
          console.error(message, error);
        }
      }
    });
  }, [
    getEffectiveTrackDuration,
    getSongUrl,
    getTargetVolume,
    hasActiveSongAnnouncements,
    isEmergencyStopped,
    isRemotePaused,
    isWebCrossfadeEnabled,
    markTrackAsFailed,
    markSongCompletedForAnnouncements,
    reportNoPlayableTracks,
    selectQueuedSong,
    showPrayerModal,
    showScheduleModal
  ]);

  // Audio element event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // PlaybackStatusService'e audio referansını ver (periyodik güncelleme için)
    playbackStatusService.setAudioRef(audio);

    const handleLoadedData = () => {
      setDuration(getEffectiveTrackDuration(audio, currentSongRef.current));
      if (currentSongRef.current?._id) {
        audioRetryRef.current[currentSongRef.current._id] = 0;
      }
      // Her şarkı yüklendiğinde volume ayarını uygula (mute durumunu da kontrol et)
      audio.volume = isMuted ? 0 : volume / 100;
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      startCrossfadeToNext();
    };

    const handleEnded = () => {
      if (playbackEngineRef.current.isTransitioning()) {
        if (playbackEngineRef.current.completeNow()) {
          return;
        }
        cleanupCrossfadeAudio();
      }

      recordEnterpriseSongPlay(enhancedPlaylistRef.current._id, currentSongRef.current);
      markSongCompletedForAnnouncements(currentSongRef.current);

      if (
        currentSongRef.current?.announcementScheduleType === 'specific' &&
        restoreInterruptedPlayback()
      ) {
        return;
      }

      if (playQueuedTimedAnnouncement()) {
        return;
      }

      playNext();
    };

    const recoverFromAudioFailure = () => {
      cleanupCrossfadeAudio();
      const failedSong = currentSongRef.current;
      if (!failedSong) return;

      const retryKey = failedSong._id || failedSong.cdnUrl || failedSong.filePath || 'unknown';
      const retries = audioRetryRef.current[retryKey] || 0;

      if (retries < 2) {
        audioRetryRef.current[retryKey] = retries + 1;
        audio.load();
        if (isPlaying) {
          audio.play().catch(() => {
            playNext();
          });
        }
        return;
      }

      audioRetryRef.current[retryKey] = 0;
      markTrackAsFailed(failedSong);
      playNext();
    };

    // Play event - Manager'a bildir ve localStorage'a kaydet
    const handlePlay = () => {
      if (isPrayerPlaybackBlocked()) {
        audio.pause();
        setIsPlaying(false);
        localStateStore.setString('playerIsPlaying', 'false');
        return;
      }

      localStateStore.setString('playerIsPlaying', 'true');
      if (currentSong) {
        const contentType = currentSong.isAnnouncement ? 'anons' : 'song';
        if (!currentSong.isAnnouncement) {
          getPlaybackQueue().recordPlay(currentSong, enhancedPlaylistRef.current._id, {
            playlist: enhancedPlaylistRef.current
          });
        }
        const playbackDuration = getEffectiveTrackDuration(audio, currentSong);
        playbackStatusService.sendPlaying(
          currentSong._id,
          currentSong.title || currentSong.name,
          currentSong.artist || 'Bilinmeyen',
          playbackDuration,
          contentType
        );
        websocketService.sendPlaylistPlaybackStarted(enhancedPlaylistRef.current);
      }
    };

    // Pause event - Manager'a bildir ve localStorage'a kaydet
    const handlePause = () => {
      if (suppressPauseEventRef.current) {
        return;
      }
      // Şarkı bittiyse (ended) pause bildirme - çünkü hemen sonraki şarkıya geçilecek
      if (audio.ended) {
        return;
      }
      localStateStore.setString('playerIsPlaying', 'false');
      // Ezan veya schedule durumu değilse normal pause bildir
      if (!showPrayerModal && !showScheduleModal) {
        playbackStatusService.sendPaused();
      }
    };

    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', recoverFromAudioFailure);
    audio.addEventListener('stalled', recoverFromAudioFailure);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    // WebSocket bağlantısı kurulduğunda mevcut durumu gönder (Manager hemen görsün)
    const handleWebsocketConnected = () => {
      if (currentSong && !audio.paused) {
        const contentType = currentSong.isAnnouncement ? 'anons' : 'song';
        const playbackDuration = getEffectiveTrackDuration(audio, currentSong);
        playbackStatusService.sendPlaying(
          currentSong._id,
          currentSong.title || currentSong.name,
          currentSong.artist || 'Bilinmeyen',
          playbackDuration,
          contentType
        );
      }
    };
    window.addEventListener('websocketConnected', handleWebsocketConnected);

    // Manager'dan getCurrentStatus komutu geldiğinde mevcut durumu gönder
    const handleRequestPlaybackStatus = () => {
      playbackStatusService.sendCurrentStatus();
    };
    window.addEventListener('requestPlaybackStatus', handleRequestPlaybackStatus);

    // Manager'dan uzaktan oynat komutu
    const handleRemotePlay = () => {
      setIsRemotePaused(false); // Manager play yaptı, uyarıyı kaldır
      if (isPrayerPlaybackBlocked()) {
        return;
      }
      if (audio.paused && currentSong) {
        audio.play().then(() => {
          setIsPlaying(true);
        }).catch(error => {
          if (!isPlayInterruptionError(error) && error.name !== 'NotAllowedError') {
            console.error(error);
          }
        });
      }
    };
    window.addEventListener('remotePlay', handleRemotePlay);

    // Manager'dan uzaktan duraklat komutu
    const handleRemotePause = () => {
      setIsRemotePaused(true); // Manager pause yaptı, uyarı göster
      cleanupCrossfadeAudio();
      if (!audio.paused) {
        audio.pause();
        setIsPlaying(false);
      }
    };
    window.addEventListener('remotePause', handleRemotePause);

    // Manager'dan uzaktan ses seviyesi değiştir
    const handleRemoteSetVolume = (event: Event) => {
      const customEvent = event as CustomEvent<{ volume: number }>;
      const newVolume = customEvent.detail.volume;
      setVolume(newVolume);
      playbackEngineRef.current.setVolume(audio, crossfadeAudioRef.current, isMuted ? 0 : newVolume / 100);
      localStateStore.setString('playerVolume', newVolume.toString());
      console.log('[Player] Ses seviyesi uzaktan değiştirildi:', newVolume);
    };
    window.addEventListener('remoteSetVolume', handleRemoteSetVolume);

    // Acil durdurma - müziği durdur ve state güncelle
    const handleEmergencyStop = (event: Event) => {
      const customEvent = event as CustomEvent<{ reason: string }>;
      const reason = customEvent.detail?.reason || 'Acil durum';
      console.log('[Player] ACİL DURDURMA:', reason);
      setIsEmergencyStopped(true);
      setEmergencyReason(reason);
      cleanupCrossfadeAudio();
      audio.pause();
      setIsPlaying(false);
    };
    window.addEventListener('emergencyStop', handleEmergencyStop);

    // Acil durum sıfırlama - müziği devam ettir
    const handleEmergencyReset = () => {
      console.log('[Player] Acil durum sıfırlandı, müzik devam ediyor');
      setIsEmergencyStopped(false);
      if (currentSong && !isPrayerPlaybackBlocked()) {
        audio.play().then(() => {
          setIsPlaying(true);
        }).catch(error => {
          if (!isPlayInterruptionError(error) && error.name !== 'NotAllowedError') {
            console.error(error);
          }
        });
      }
    };
    window.addEventListener('emergencyReset', handleEmergencyReset);

    return () => {
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', recoverFromAudioFailure);
      audio.removeEventListener('stalled', recoverFromAudioFailure);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      window.removeEventListener('websocketConnected', handleWebsocketConnected);
      window.removeEventListener('requestPlaybackStatus', handleRequestPlaybackStatus);
      window.removeEventListener('remotePlay', handleRemotePlay);
      window.removeEventListener('remotePause', handleRemotePause);
      window.removeEventListener('remoteSetVolume', handleRemoteSetVolume);
      window.removeEventListener('emergencyStop', handleEmergencyStop);
      window.removeEventListener('emergencyReset', handleEmergencyReset);
      playbackStatusService.setAudioRef(null);
      cleanupCrossfadeAudio();
    };
  }, [playNext, playQueuedTimedAnnouncement, restoreInterruptedPlayback, markSongCompletedForAnnouncements, markTrackAsFailed, getPlaybackQueue, getEffectiveTrackDuration, volume, isMuted, isPlaying, currentSong, showPrayerModal, showScheduleModal, startCrossfadeToNext, cleanupCrossfadeAudio]); // currentSong ve modal state'leri eklendi

  // Şarkı değiştiğinde yeni şarkıyı yükle - SADECE GERÇEK DEĞIŞIKLIKTE
  useEffect(() => {
    if (currentSong && audioRef.current) {
      if (audioRef.current && !audioRef.current.paused && 
          currentlyPlayingTrackRef.current?.name === currentSong?.name) {
        return;
      }
      
      if (currentlyPlayingTrackRef.current?._id !== currentSong._id) {
        currentlyPlayingTrackRef.current = currentSong;
        
        const songUrl = resolveSongUrl(currentSong, playlist.baseUrl);

        if (!songUrl) {
          markTrackAsFailed(currentSong);
          playNext();
          return;
        }
        
        // State'leri resetle - anons sonrası kart sorunu için kritik
        setCurrentTime(0);
        setDuration(0);
        
        audioRef.current.src = songUrl;
        audioRef.current.load();
        // Volume ayarını uygula (mute durumunu da kontrol et)
        audioRef.current.volume = isMuted ? 0 : volume / 100;

        const pendingResume = pendingResumeRef.current?.songId === currentSong._id
          ? pendingResumeRef.current
          : null;
        const forceTimedAnnouncementPlayback =
          Boolean(currentSong.isAnnouncement &&
            currentSong.scheduledAnnouncementKey &&
            forceTimedAnnouncementPlaybackRef.current === currentSong.scheduledAnnouncementKey);

        const clearOneShotPlaybackFlags = () => {
          if (pendingResumeRef.current?.songId === currentSong._id) {
            pendingResumeRef.current = null;
          }

          if (
            currentSong.scheduledAnnouncementKey &&
            forceTimedAnnouncementPlaybackRef.current === currentSong.scheduledAnnouncementKey
          ) {
            forceTimedAnnouncementPlaybackRef.current = null;
          }
        };

        const applyPendingResume = () => {
          if (!pendingResume || !audioRef.current) {
            return;
          }

          const safeTime = Number.isFinite(pendingResume.time) && pendingResume.time > 0
            ? Math.min(pendingResume.time, audioRef.current.duration || pendingResume.time)
            : 0;

          audioRef.current.currentTime = safeTime;
          setCurrentTime(safeTime);
        };

        if (pendingResume) {
          if (audioRef.current.readyState >= 1) {
            applyPendingResume();
          } else {
            audioRef.current.addEventListener('loadedmetadata', applyPendingResume, { once: true });
          }
        }

        // localStorage'dan önceki çalma durumunu kontrol et
        // Eğer kullanıcı müziği durdurmuşsa, sayfa yenilendiğinde otomatik başlatma
        const savedIsPlaying = localStateStore.getString('playerIsPlaying');
        const isRemotePaused = localStateStore.getString('remotePaused') === 'true';
        let shouldForceAssignedPlaylist = false;
        try {
          const pendingRaw = localStateStore.getString('pendingPlaylistAutoplay');
          const pendingAutoplay = pendingRaw ? JSON.parse(pendingRaw) : null;
          shouldForceAssignedPlaylist = Boolean(
            pendingAutoplay?.playlistId &&
            pendingAutoplay.playlistId === playlist?._id &&
            !isRemotePaused
          );
        } catch {
          localStateStore.remove('pendingPlaylistAutoplay');
        }
        // Manager'dan pause yapılmışsa veya kullanıcı durdurmuşsa çalma
        const isPrayerActive = showPrayerModal || prayerTimeService.getCurrentState().isPrayerTime;
        const shouldAutoPlay = !isPrayerActive && (
          forceTimedAnnouncementPlayback ||
          shouldForceAssignedPlaylist ||
          (pendingResume ? pendingResume.shouldPlay : savedIsPlaying !== 'false' && !isRemotePaused)
        );

        if (shouldAutoPlay) {
          const playbackAudio = audioRef.current;
          const attemptId = ++playAttemptRef.current;
          playbackAudio.play().then(() => {
            clearOneShotPlaybackFlags();
            if (playAttemptRef.current !== attemptId || audioRef.current !== playbackAudio) {
              return;
            }
            setIsPlaying(true);
          }).catch(error => {
            clearOneShotPlaybackFlags();
            if (
              playAttemptRef.current !== attemptId ||
              audioRef.current !== playbackAudio ||
              isPlayInterruptionError(error)
            ) {
              return;
            }

            // Autoplay policy nedeniyle hata - sessizce yakala
            if (error.name === 'NotAllowedError') {
              if (shouldForceAssignedPlaylist) {
                websocketService.sendPlaylistPlaybackBlocked(enhancedPlaylistRef.current, 'autoplay_blocked');
              }
              // Kullanıcı etkileşimi gerekiyor, sessizce bekle
              setIsPlaying(false);
            } else {
              // Diğer hatalar için log
              console.error('Audio çalma hatası:', error);
              setIsPlaying(false);
            }
          });
        } else {
          // Müzik durmuş durumda kalacak
          clearOneShotPlaybackFlags();
          setIsPlaying(false);
        }
      }
    }
  }, [currentSong, playlist, markTrackAsFailed, playNext, showPrayerModal]);

  // Audio element yüklendiğinde saved volume'u uygula
  useEffect(() => {
    playbackEngineRef.current.setVolume(
      audioRef.current,
      crossfadeAudioRef.current,
      isMuted ? 0 : volume / 100
    );
  }, [volume, isMuted]);

  // F9 tuşu ile son 20 saniyeye atlama
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'F9') {
        event.preventDefault();
        
        // Debug: F9 algılandı mı?
        const audioDuration = getEffectiveTrackDuration(audioRef.current, currentSongRef.current);
        
        if (audioRef.current && audioDuration > 20) {
          const jumpToTime = audioDuration - 20; // Son 20 saniye
          audioRef.current.currentTime = jumpToTime;
          setCurrentTime(jumpToTime);
        } else if (audioRef.current && audioDuration <= 20 && audioDuration > 0) {
          console.log('Şarkı 20 saniyeden kısa, başa sarılıyor');
          audioRef.current.currentTime = 0;
          setCurrentTime(0);
        } else {
          console.log('Audio hazır değil veya duration yok:', { 
            audio: !!audioRef.current, 
            stateDuration: duration,
            audioDuration: audioDuration,
            isNaN: isNaN(audioDuration) 
          });
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [duration]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      cleanupCrossfadeAudio();
      audio.pause();
      setIsPlaying(false);
      // Pause edildiğinde ref'i temizleme - sadece durduruluyor
    } else {
      // Manager pause yaptıysa kullanıcı play yapamaz
      const isRemotePaused = localStateStore.getString('remotePaused') === 'true';
      if (isRemotePaused) {
        console.log('[Player] Manager tarafından duraklatıldı, play engellendi');
        return;
      }
      if (isPrayerPlaybackBlocked()) {
        console.log('[Player] Ezan vakti aktif, play engellendi');
        return;
      }
      audio.play().then(() => {
        setIsPlaying(true);
        // Çalmaya başladığında current track ref'i güncelle
        if (currentSong) {
          currentlyPlayingTrackRef.current = currentSong;
        }
      }).catch(error => {
        if (!isPlayInterruptionError(error) && error.name !== 'NotAllowedError') {
          console.error('Oynatma hatası:', error);
        }
      });
    }
  };

  const volumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseInt(e.target.value);
    setVolume(newVolume);
    
    // Eğer volume artırılırsa mute'u kaldır
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
      localStateStore.setString('playerMuted', 'false');
    }
    
    // Volume değerini localStorage'a kaydet
    localStateStore.setString('playerVolume', newVolume.toString());
    
    playbackEngineRef.current.setVolume(
      audioRef.current,
      crossfadeAudioRef.current,
      (newVolume > 0 ? false : isMuted) ? 0 : newVolume / 100
    );
    
    // Desktop tarzı dinamik renk sistemi ile volume bar güncellemesi
    const volumeColor = gradientColors.isDark 
      ? `rgba(${gradientColors.tertiary}, 0.9)` // Koyu artwork → Açık renk
      : `rgba(${gradientColors.primary}, 0.8)`; // Açık artwork → Koyu renk
    
    e.target.style.background = `linear-gradient(to right, ${volumeColor} 0%, ${volumeColor} ${newVolume}%, rgba(255,255,255,0.2) ${newVolume}%, rgba(255,255,255,0.2) 100%)`;
  };
  
  // Mute toggle
  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    
    // Audio elementine uygula
    playbackEngineRef.current.setVolume(
      audioRef.current,
      crossfadeAudioRef.current,
      newMutedState ? 0 : volume / 100
    );
    
    // localStorage'a kaydet
    localStateStore.setString('playerMuted', newMutedState.toString());
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Certificate indirme fonksiyonu
  const downloadCertificate = () => {
    if (!certificateData) return;
    
    console.log('Certificate indiriliyor:', certificateData.certificateUrl);
    
    // Yeni bir link elementi oluştur ve indirmeyi başlat
    const link = document.createElement('a');
    link.href = certificateData.certificateUrl;
    link.download = certificateData.title + '.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // İndirme sonrası certificate'i gizle
    setCertificateData(null);
  };


  const getArtworkUrl = () => {
    return enhancedPlaylist.artwork || null;
  };

  if (!currentSong) return null;

  // Stable gradient style with new color extraction system
  const gradientStyle = {
    background: colorResult?.backgroundGradient || 
               `linear-gradient(135deg, 
                rgba(${gradientColors.primary}, ${gradientColors.primaryOpacity}) 0%, 
                rgba(${gradientColors.primary}, ${gradientColors.primaryOpacity}) 30%,
                rgba(${gradientColors.secondary}, ${gradientColors.secondaryOpacity}) 60%, 
                rgba(${gradientColors.tertiary}, ${gradientColors.tertiaryOpacity}) 100%)`
  };
  

  // ========================================
  // MOBILE NATIVE PLAYER - TEK RETURN, CSS TOGGLE
  // Audio elementi unmount olmaz, orientation değişince müzik durmaz
  // ========================================
  if (isMobile) {
    return (
      <>
        {/* TEK AUDIO - Her zaman DOM'da kalır */}
        <audio ref={audioRef} preload="auto" />
        <audio ref={crossfadeAudioRef} preload="auto" />

        {/* Mobile Landscape Layout - CSS ile göster/gizle */}
        <div
          className="fixed inset-0 flex flex-row text-white select-none"
          style={{
            ...gradientStyle,
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
            display: isLandscape ? 'flex' : 'none'
          }}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        >
          {/* Sol Taraf - Artwork */}
          <div className="flex items-center justify-center p-4" style={{ width: '40%' }}>
            <div
              className="rounded-xl overflow-hidden shadow-2xl"
              style={{
                width: Math.min(windowHeight - 32, 200),
                height: Math.min(windowHeight - 32, 200)
              }}
            >
              {getArtworkUrl() ? (
                <img
                  src={getArtworkUrl()!}
                  alt={currentSong.title || currentSong.name}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              ) : (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center text-4xl">
                  🎵
                </div>
              )}
            </div>
          </div>

          {/* Sağ Taraf - Bilgiler ve Kontroller */}
          <div className="flex-1 flex flex-col justify-center p-4" style={{ width: '60%' }}>
            {/* Song Info */}
            <div className="mb-4">
              <p className="text-xs opacity-70 mb-1">{enhancedPlaylist.name}</p>
              <h3 className="text-lg font-semibold mb-0.5 line-clamp-1">
                {currentSong.title || currentSong.name}
              </h3>
              <p className="text-sm opacity-70 line-clamp-1">
                {currentSong.artist || 'Unknown Artist'}
              </p>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-4 mb-4">
              <span className="text-xs opacity-70 min-w-[35px]">{formatTime(currentTime)}</span>
              <button
                onClick={togglePlay}
                className="p-3 rounded-full text-black flex-shrink-0"
                style={{ backgroundColor: '#ffed00' }}
              >
                {isPlaying ? <Pause size={24} /> : <Play size={24} />}
              </button>
              <span className="text-xs opacity-70 min-w-[35px]">{formatTime(duration)}</span>
            </div>

            {/* Volume */}
            <div className="flex items-center gap-3 bg-white/10 rounded-xl px-3 py-2">
              <button onClick={toggleMute} className="flex-shrink-0">
                {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={handleVolumeChange}
                className="flex-1 h-2 bg-white/20 rounded-full appearance-none cursor-pointer
                           [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer
                           [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0"
                style={{
                  background: `rgba(255,255,255,0.2)`
                }}
              />
              <span className="text-xs min-w-[28px] text-center">{volume}%</span>
            </div>

            {/* VEEQ Logo */}
            <div className="mt-4">
              <span className="font-bold text-xl text-[#ffed00]">veeq</span>
            </div>
          </div>
        </div>

        {/* Mobile Portrait Layout - CSS ile göster/gizle */}
        <div
          className="fixed inset-0 flex flex-col text-white select-none"
          style={{
            ...gradientStyle,
            paddingTop: 'max(16px, env(safe-area-inset-top))',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
            display: isLandscape ? 'none' : 'flex'
          }}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        >
          {/* Tüm içerik - tek grup, ortada, eşit aralıklı */}
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
            {/* Artwork */}
            <div
              className="rounded-2xl overflow-hidden shadow-2xl"
              style={{
                width: Math.min(windowWidth - 64, windowHeight - 300),
                height: Math.min(windowWidth - 64, windowHeight - 300)
              }}
            >
              {getArtworkUrl() ? (
                <img
                  src={getArtworkUrl()!}
                  alt={currentSong.title || currentSong.name}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              ) : (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center text-6xl">
                  🎵
                </div>
              )}
            </div>

            {/* Song Info */}
            <div className="text-center w-full">
              <p className="text-xs opacity-70 mb-1">{enhancedPlaylist.name}</p>
              <h3 className="text-lg font-semibold mb-0.5 line-clamp-1">
                {currentSong.title || currentSong.name}
              </h3>
              <p className="text-sm opacity-70 line-clamp-1">
                {currentSong.artist || 'Unknown Artist'}
              </p>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4">
              <span className="text-xs opacity-70 min-w-[35px] text-right">{formatTime(currentTime)}</span>
              <button
                onClick={togglePlay}
                className="p-3 rounded-full text-black"
                style={{ backgroundColor: '#ffed00' }}
              >
                {isPlaying ? <Pause size={24} /> : <Play size={24} />}
              </button>
              <span className="text-xs opacity-70 min-w-[35px] text-left">{formatTime(duration)}</span>
            </div>

            {/* Volume */}
            <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2 max-w-[240px] w-full">
              <button onClick={toggleMute} className="flex-shrink-0">
                {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={handleVolumeChange}
                className="flex-1 h-2 bg-white/20 rounded-full appearance-none cursor-pointer
                           [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer
                           [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0"
                style={{
                  background: `rgba(255,255,255,0.2)`
                }}
              />
              <span className="text-xs min-w-[30px] text-center">{volume}%</span>
            </div>

            {/* VEEQ Logo */}
            <div className="text-center">
              <span className="font-bold text-3xl text-[#ffed00]">veeq</span>
            </div>
          </div>
        </div>

        {/* Modals - Her iki layout için ortak */}
        {showPrayerModal && prayerTimeInfo && <PrayerTimeModal prayerInfo={prayerTimeInfo} />}
        {showScheduleModal && scheduleState.active && (
          <MusicScheduleModal scheduleInfo={{ message: scheduleState.message, days: musicSchedule?.days }} />
        )}
        {isEmergencyStopped && <EmergencyStopModal reason={emergencyReason} />}
        {isRemotePaused && !isEmergencyStopped && <RemotePausedBanner />}
      </>
    );
  }

  // ========================================
  // DESKTOP PLAYER (Mevcut tasarım)
  // ========================================
  return (
    <div
      className={`fixed inset-0 flex flex-col items-center justify-center text-white select-none ${isShortViewport ? 'p-4' : 'p-8'}`}
      style={gradientStyle}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      {/* Certificate İndirme Butonu - Sol Alt - Sadece Desktop */}
      {certificateData && (
        <button
          onClick={downloadCertificate}
          className="absolute bottom-8 left-8 p-4 rounded-full backdrop-blur-md transition-all duration-300 hover:scale-110"
          style={{ backgroundColor: `rgba(${gradientColors.primary}, 0.8)` }}
          title={`Sertifikayı İndir: ${certificateData.title}`}
        >
          <Download size={24} className="text-white" />
        </button>
      )}

      {/* Artwork */}
      <div
        className={`rounded-2xl overflow-hidden shadow-2xl relative ${isShortViewport ? 'mb-4' : 'w-72 h-72 lg:w-80 lg:h-80 mb-6'}`}
        style={isShortViewport ? {
          width: Math.min(windowHeight * 0.35, 256),
          height: Math.min(windowHeight * 0.35, 256)
        } : undefined}
      >
        {getArtworkUrl() ? (
          <img
            src={getArtworkUrl()!}
            alt={currentSong.title || currentSong.name}
            className="w-full h-full object-cover"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          />
        ) : (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center text-6xl">
            🎵
          </div>
        )}
      </div>

      {/* Song Info */}
      <div className={`text-center max-w-md ${isShortViewport ? 'mb-4' : 'mb-10'}`}>
        <p className={`opacity-90 font-medium ${isShortViewport ? 'text-sm mb-1' : 'text-base mb-3'}`}>{enhancedPlaylist.name}</p>
        <h3 className={`font-semibold mb-1 ${isShortViewport ? 'text-lg' : 'text-2xl mb-2'}`}>{currentSong.title || currentSong.name}</h3>
        <p className={`opacity-80 ${isShortViewport ? 'text-sm' : 'text-lg'}`}>{currentSong.artist || 'Unknown Artist'}</p>
      </div>

      {/* Controls */}
      <div className={`flex items-center justify-center gap-6 ${isShortViewport ? 'mb-3' : 'mb-6'}`}>
        <span className="text-sm text-white/80 min-w-[40px] text-right">{formatTime(currentTime)}</span>
        <button
          onClick={togglePlay}
          className="p-4 rounded-full text-black"
          style={{ backgroundColor: '#ffed00' }}
        >
          {isPlaying ? <Pause size={32} /> : <Play size={32} />}
        </button>
        <span className="text-sm text-white/80 min-w-[40px] text-left">{formatTime(duration)}</span>
      </div>

      {/* Volume */}
      <div className={`w-72 lg:w-80 flex items-center gap-4 bg-white/5 rounded-2xl px-5 backdrop-blur-md group ${isShortViewport ? 'py-2 mb-4' : 'py-3 mb-10'}`}>
        <button onClick={toggleMute}>
          {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
        <input
          type="range"
          min="0"
          max="100"
          value={volume}
          onChange={handleVolumeChange}
          className="flex-1 h-2 bg-white/20 rounded-full appearance-none cursor-pointer
                     [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5
                     [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer
                     [&::-webkit-slider-thumb]:opacity-0 [&::-webkit-slider-thumb]:transition-opacity
                     group-hover:[&::-webkit-slider-thumb]:opacity-100
                     [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5
                     [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer
                     [&::-moz-range-thumb]:opacity-0 [&::-moz-range-thumb]:transition-opacity
                     group-hover:[&::-moz-range-thumb]:opacity-100"
          onMouseEnter={(e) => {
            const volumeColor = gradientColors.isDark
              ? `rgba(${gradientColors.tertiary}, 0.9)`
              : `rgba(${gradientColors.primary}, 0.8)`;
            (e.target as HTMLElement).style.background = `linear-gradient(to right, ${volumeColor} 0%, ${volumeColor} ${volume}%, rgba(255,255,255,0.2) ${volume}%, rgba(255,255,255,0.2) 100%)`;
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLElement).style.background = `rgba(255,255,255,0.2)`;
          }}
          style={{ background: `rgba(255,255,255,0.2)` }}
        />
        <span className="text-sm min-w-[38px] text-center font-medium">{volume}%</span>
      </div>

      {/* VEEQ Logo */}
      <div className="text-center select-none">
        <span
          className={`font-bold text-[#ffed00] ${isShortViewport ? 'text-4xl' : 'text-5xl'}`}
          style={{ letterSpacing: '-1px' }}
          onContextMenu={(e) => e.preventDefault()}
        >
          veeq
        </span>
      </div>

      <audio ref={audioRef} preload="auto" />
      <audio ref={crossfadeAudioRef} preload="auto" />
      {showPrayerModal && prayerTimeInfo && <PrayerTimeModal prayerInfo={prayerTimeInfo} />}
      {showScheduleModal && scheduleState.active && (
        <MusicScheduleModal scheduleInfo={{ message: scheduleState.message, days: musicSchedule?.days }} />
      )}
      {isEmergencyStopped && <EmergencyStopModal reason={emergencyReason} />}
      {isRemotePaused && !isEmergencyStopped && <RemotePausedBanner />}
    </div>
  );
};

// Ezan Vakti Modal Component
const PrayerTimeModal: React.FC<{ prayerInfo: { prayerName: string; endTime: Date } }> = ({ prayerInfo }) => {
  const [remainingTime, setRemainingTime] = useState({ minutes: 0, seconds: 0 });

  useEffect(() => {
    const updateRemainingTime = () => {
      const remainingSeconds = Math.max(0, (prayerInfo.endTime.getTime() - Date.now()) / 1000);
      const minutes = Math.floor(remainingSeconds / 60);
      const seconds = Math.floor(remainingSeconds % 60);
      setRemainingTime({ minutes, seconds });
    };

    // İlk güncelleme
    updateRemainingTime();

    // Her saniye güncelle
    const interval = setInterval(updateRemainingTime, 1000);

    return () => clearInterval(interval);
  }, [prayerInfo.endTime]);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: '#ffed00' }}>
      <div className="w-full max-w-[420px] bg-black backdrop-blur-sm border border-black rounded-lg">
        <div className="space-y-3 flex flex-col items-center py-6 px-6 sm:py-8 sm:px-8">
          {/* Veeq Logo - Animasyonlu */}
          <div className="mb-3 sm:mb-4">
            <div className="font-bold text-3xl sm:text-4xl text-[#ffed00]" style={{ letterSpacing: '-1px' }}>
              {['v', 'e', 'e', 'q'].map((letter, index) => (
                <span
                  key={index}
                  style={{
                    animation: `letterFade 1.8s infinite ${index * 0.2}s`,
                    display: 'inline-block'
                  }}
                >
                  {letter}
                </span>
              ))}
              <style>{`
                @keyframes letterFade {
                  0%, 100% { opacity: 1; transform: translateY(0); }
                  25% { opacity: 0.3; transform: translateY(-8px); }
                  50% { opacity: 1; transform: translateY(0); }
                }
              `}</style>
            </div>
          </div>

          {/* Başlık */}
          <h2 className="text-xl sm:text-2xl text-white text-center font-semibold">
            {prayerInfo.prayerName} Vakti
          </h2>

          {/* Açıklama */}
          <p className="text-gray-400 text-center text-sm sm:text-base">
            Ezan vakti dolayısıyla müzik duraklatıldı
          </p>
        </div>

        <div className="px-6 pb-6 sm:px-8 sm:pb-8">
          <div className="text-center">
            <p className="text-white text-base sm:text-lg">
              Müzik otomatik olarak devam edecek
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Planlı Çalma Modal Component (Ezan Vakti Modal gibi)
const MusicScheduleModal: React.FC<{ scheduleInfo: { message: string; days?: string[] } }> = ({ scheduleInfo }) => {
  // Gün adlarını Türkçe'ye çevir
  const dayNamesTR: Record<string, string> = {
    'sunday': 'Pazar',
    'monday': 'Pazartesi',
    'tuesday': 'Salı',
    'wednesday': 'Çarşamba',
    'thursday': 'Perşembe',
    'friday': 'Cuma',
    'saturday': 'Cumartesi'
  };

  const daysText = scheduleInfo.days?.map(day => dayNamesTR[day] || day).join(', ') || '';

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: '#1a1a2e' }}>
      <div className="w-full max-w-[420px] bg-black backdrop-blur-sm border border-gray-800 rounded-lg">
        <div className="space-y-3 flex flex-col items-center py-6 px-6 sm:py-8 sm:px-8">
          {/* Veeq Logo - Animasyonlu */}
          <div className="mb-3 sm:mb-4">
            <div className="font-bold text-3xl sm:text-4xl text-[#ffed00]" style={{ letterSpacing: '-1px' }}>
              {['v', 'e', 'e', 'q'].map((letter, index) => (
                <span
                  key={index}
                  style={{
                    animation: `letterFade 1.8s infinite ${index * 0.2}s`,
                    display: 'inline-block'
                  }}
                >
                  {letter}
                </span>
              ))}
              <style>{`
                @keyframes letterFade {
                  0%, 100% { opacity: 1; transform: translateY(0); }
                  25% { opacity: 0.3; transform: translateY(-8px); }
                  50% { opacity: 1; transform: translateY(0); }
                }
              `}</style>
            </div>
          </div>

          {/* Başlık */}
          <h2 className="text-xl sm:text-2xl text-white text-center font-semibold">
            Planlı Çalma
          </h2>

          {/* Açıklama */}
          <p className="text-gray-400 text-center text-sm sm:text-base">
            Şu an müzik çalma saatleri dışındasınız
          </p>
        </div>

        <div className="px-6 pb-6 sm:px-8 sm:pb-8">
          <div className="space-y-4">
            {/* Saat Bilgisi */}
            <div className="text-center bg-gray-900 rounded-lg py-3 px-3 sm:py-4 sm:px-4">
              <p className="text-yellow-400 text-base sm:text-lg font-medium">
                {scheduleInfo.message}
              </p>
            </div>

            {/* Aktif Günler */}
            {daysText && (
              <div className="text-center">
                <p className="text-gray-500 text-xs sm:text-sm">Aktif Günler:</p>
                <p className="text-white text-xs sm:text-sm mt-1">{daysText}</p>
              </div>
            )}

            {/* Bilgi */}
            <div className="text-center mt-4">
              <p className="text-gray-400 text-xs sm:text-sm">
                Müzik belirlenen saatlerde otomatik olarak başlayacak
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Acil Durdurma Modal Component (PrayerTimeModal stilinde)
const EmergencyStopModal: React.FC<{ reason: string }> = ({ reason }) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: '#8B0000' }}>
      <div className="w-full max-w-[420px] bg-black backdrop-blur-sm border border-red-900 rounded-lg">
        <div className="space-y-3 flex flex-col items-center py-6 px-6 sm:py-8 sm:px-8">
          {/* Veeq Logo - Animasyonlu */}
          <div className="mb-3 sm:mb-4">
            <div className="font-bold text-3xl sm:text-4xl text-red-500" style={{ letterSpacing: '-1px' }}>
              {['v', 'e', 'e', 'q'].map((letter, index) => (
                <span
                  key={index}
                  style={{
                    animation: `emergencyPulse 1.5s infinite ${index * 0.15}s`,
                    display: 'inline-block'
                  }}
                >
                  {letter}
                </span>
              ))}
              <style>{`
                @keyframes emergencyPulse {
                  0%, 100% { opacity: 1; transform: scale(1); }
                  50% { opacity: 0.5; transform: scale(1.1); }
                }
              `}</style>
            </div>
          </div>

          {/* Uyarı İkonu */}
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-600/20 flex items-center justify-center mb-2">
            <svg className="w-10 h-10 sm:w-12 sm:h-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>

          {/* Başlık */}
          <h2 className="text-xl sm:text-2xl text-white text-center font-semibold">
            MÜZİK DURDURULDU
          </h2>

          {/* Açıklama */}
          <p className="text-gray-400 text-center text-sm sm:text-base">
            Acil durdurma aktif
          </p>
        </div>

        <div className="px-6 pb-6 sm:px-8 sm:pb-8">
          {/* Sebep */}
          <div className="text-center bg-red-900/30 rounded-lg py-3 px-3 sm:py-4 sm:px-4 border border-red-800/50">
            <p className="text-red-400 text-sm sm:text-base font-medium">
              {reason}
            </p>
          </div>

          <div className="text-center mt-4">
            <p className="text-gray-500 text-xs sm:text-sm">
              Sistem tarafından yayınınız geçici olarak durdurulmuştur
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Manager Tarafından Duraklatıldı Banner
// Mobile: üstte, Desktop: altta
const RemotePausedBanner: React.FC = () => {
  return (
    <div className="fixed left-0 right-0 z-50 bg-amber-600/95 backdrop-blur-sm py-3 px-4 shadow-lg top-0 md:top-auto md:bottom-0">
      <div className="flex items-center justify-center gap-3">
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-white font-medium text-sm">
          Müzik yönetici tarafından duraklatıldı
        </span>
      </div>
    </div>
  );
};

export default Player;
